import { useState, useEffect, useMemo } from 'react';
import api from '../api';
import { formatBusinessDate } from '../utils/casinoDay';
import {
  FileText,
  RefreshCw,
  Calendar,
  Clock,
  Users,
  BarChart3,
  Table2,
  ChevronDown,
  ChevronRight,
  Download,
  ArrowUpDown,
  TrendingUp,
  Trophy,
  Gamepad2,
  Gift,
  UserCheck,
  AlertTriangle,
  Grid3X3,
  X,
  User,
  CreditCard,
  Mail,
  Phone,
  Crown,
  Star,
  Award,
  Zap,
  Check,
  Lock,
  DollarSign,
  Sliders,
  MapPin,
  Info,
} from 'lucide-react';
import toast from 'react-hot-toast';
import DiscountReport from './DiscountReport';
import RewardLift from './RewardLift';
import GeofenceReport from './GeofenceReport';
import FreePlayByMonth from './FreePlayByMonth';
import PlayerDetailModal from '../components/PlayerDetailModal';
import WinbackAlgorithmConfig from '../components/WinbackAlgorithmConfig';
import {
  DEFAULT_WINBACK_CONFIG,
  computeValueScore,
  getRecencyMultiplier,
  computeFinalScore,
  computeComp,
  getCompColor,
} from '../lib/winbackAlgorithm';

// Map Ionicon names to emojis for achievement display
const TIER_LEVEL_NAMES = { '1': 'VIP', '2': 'Elite', '3': 'Ambassador', '4': 'Boardmember', '5': 'Chairman', '6': 'Industry' };
const TIER_KEY_NAMES = { vip: 'VIP', elite: 'Elite', ambassador: 'Ambassador', boardmember: 'Boardmember', chairman: 'Chairman', legend: 'Chairman', industry: 'Industry' };
// Canonical tier display name. TableCaptain sends tier as a number 1-5; the app also uses
// tier keys (vip/elite/...) and display names. Normalize any of them to one label so every
// report is consistent. (1=VIP 2=Elite 3=Ambassador 4=Boardmember 5=Chairman 6=Industry.)
function resolveTierName(value) {
  if (value === null || value === undefined || value === '') return 'VIP';
  const str = String(value).trim();
  if (Object.prototype.hasOwnProperty.call(TIER_LEVEL_NAMES, str)) return TIER_LEVEL_NAMES[str];
  const key = str.toLowerCase();
  if (TIER_KEY_NAMES[key]) return TIER_KEY_NAMES[key];
  return str;
}
// Tailwind badge classes keyed off the resolved tier name (shared across report tables).
function tierBadgeClass(value) {
  const n = resolveTierName(value).toLowerCase();
  if (n.includes('chairman')) return 'bg-purple-500/15 text-purple-300 border border-purple-500/40';
  if (n.includes('boardmember')) return 'bg-slate-400/15 text-slate-200 border border-slate-400/40';
  if (n.includes('ambassador')) return 'bg-yellow-500/15 text-yellow-300 border border-yellow-500/40';
  if (n.includes('industry')) return 'bg-emerald-500/15 text-emerald-300 border border-emerald-500/40';
  if (n.includes('elite')) return 'bg-gray-400/15 text-gray-300 border border-gray-400/40';
  return 'bg-amber-500/15 text-amber-300 border border-amber-500/40';
}

const ICON_MAP = {
  'footsteps': '\u{1F463}',
  'calendar': '\u{1F4C5}',
  'calendar-outline': '\u{1F5D3}\uFE0F',
  'star': '\u2B50',
  'star-outline': '\u2B50',
  'time': '\u23F1\uFE0F',
  'time-outline': '\u23F0',
  'trophy': '\u{1F3C6}',
  'trophy-outline': '\u{1F3C6}',
  'medal': '\u{1F947}',
  'medal-outline': '\u{1F3C5}',
  'ribbon': '\u{1F380}',
  'flame': '\u{1F525}',
  'flash': '\u26A1',
  'diamond': '\u{1F48E}',
  'diamond-outline': '\u{1F48E}',
  'grid': '\u{1F3B0}',
  'grid-outline': '\u{1F3B2}',
  'apps': '\u{1F4F1}',
  'apps-outline': '\u{1F4F1}',
  'cash': '\u{1F4B5}',
  'cash-outline': '\u{1F4B0}',
  'card': '\u{1F0CF}',
  'card-outline': '\u{1F3B4}',
  'heart': '\u2764\uFE0F',
  'sparkles': '\u2728',
  'rocket': '\u{1F680}',
  'crown': '\u{1F451}',
  'shield': '\u{1F6E1}\uFE0F',
  'shield-checkmark': '\u{1F6E1}\uFE0F',
  'timer': '\u23F2\uFE0F',
  'timer-outline': '\u23F2\uFE0F',
  'hourglass': '\u23F3',
  'hourglass-outline': '\u23F3',
  'checkmark-circle': '\u2705',
  'checkmark-circle-outline': '\u2705',
  'people': '\u{1F465}',
  'people-outline': '\u{1F465}',
  'game-controller': '\u{1F3AE}',
  'game-controller-outline': '\u{1F3AE}',
};

const getAchievementIcon = (iconName) => {
  return ICON_MAP[iconName] || ICON_MAP[iconName?.replace('-outline', '')] || '\u{1F3C6}';
};

// Quick date presets
const DATE_PRESETS = [
  { label: 'Today', value: 'today' },
  { label: 'Yesterday', value: 'yesterday' },
  { label: 'Last 7 Days', value: 'week' },
  { label: 'Last 30 Days', value: 'month' },
  { label: 'Last 90 Days', value: 'last90' },
  { label: 'This Week', value: 'thisWeek' },
  { label: 'This Month', value: 'thisMonth' },
  { label: 'Last Month', value: 'lastMonth' },
  { label: 'This Quarter', value: 'thisQuarter' },
  { label: 'YTD', value: 'ytd' },
  { label: 'Last Year', value: 'lastYear' },
  { label: 'Custom', value: 'custom' },
];

// Report tabs
const REPORT_TABS = [
  { id: 'table-activity', label: 'Table Activity', icon: Table2 },
  { id: 'player-activity', label: 'Top Players', icon: Trophy },
  { id: 'player-xp', label: 'Player XP', icon: Crown },
  { id: 'game-performance', label: 'Game Performance', icon: Gamepad2 },
  { id: 'player-retention', label: 'Player Retention', icon: UserCheck },
  { id: 'lapsed-players', label: 'Win-Back Analysis', icon: DollarSign },
  { id: 'hourly-heatmap', label: 'Hourly Heatmap', icon: Grid3X3 },
  { id: 'discounts', label: 'Rewards', icon: Gift },
  { id: 'reward-lift', label: 'Reward Lift', icon: TrendingUp },
  { id: 'free-play-month', label: 'Free Play by Month', icon: BarChart3 },
  { id: 'geofence-entries', label: 'Geofence Entries', icon: MapPin },
];

export default function Reports() {
  const [activeTab, setActiveTab] = useState('table-activity');
  const [loading, setLoading] = useState(true);
  const [reportData, setReportData] = useState(null);
  const [datePreset, setDatePreset] = useState('today');
  const [customStartDate, setCustomStartDate] = useState('');
  const [customEndDate, setCustomEndDate] = useState('');
  const [startTime, setStartTime] = useState('10:00');
  const [endTime, setEndTime] = useState('06:00');
  const [useBusinessHours, setUseBusinessHours] = useState(true);
  const [sortField, setSortField] = useState('totalActiveSeconds');
  const [sortDirection, setSortDirection] = useState('desc');
  const [playerLimit, setPlayerLimit] = useState(25);
  const [selectedTable, setSelectedTable] = useState(null);
  const [tablePlayersData, setTablePlayersData] = useState(null);
  const [tablePlayersLoading, setTablePlayersLoading] = useState(false);
  const [selectedPlayer, setSelectedPlayer] = useState(null);
  const [availableGames, setAvailableGames] = useState([]);
  const [selectedGames, setSelectedGames] = useState([]);
  const [showGameFilter, setShowGameFilter] = useState(false);

  // Calculate actual dates based on preset
  const getDateRange = () => {
    const today = new Date();
    const formatDate = (d) => {
      const year = d.getFullYear();
      const month = String(d.getMonth() + 1).padStart(2, '0');
      const day = String(d.getDate()).padStart(2, '0');
      return `${year}-${month}-${day}`;
    };

    switch (datePreset) {
      case 'today':
        return { startDate: formatDate(today), endDate: formatDate(today) };
      case 'yesterday': {
        const yesterday = new Date(today);
        yesterday.setDate(yesterday.getDate() - 1);
        return { startDate: formatDate(yesterday), endDate: formatDate(yesterday) };
      }
      case 'week': {
        const weekAgo = new Date(today);
        weekAgo.setDate(weekAgo.getDate() - 6);
        return { startDate: formatDate(weekAgo), endDate: formatDate(today) };
      }
      case 'month': {
        const monthAgo = new Date(today);
        monthAgo.setDate(monthAgo.getDate() - 29);
        return { startDate: formatDate(monthAgo), endDate: formatDate(today) };
      }
      case 'last90': {
        const ninetyAgo = new Date(today);
        ninetyAgo.setDate(ninetyAgo.getDate() - 89);
        return { startDate: formatDate(ninetyAgo), endDate: formatDate(today) };
      }
      case 'thisWeek': {
        const startOfWeek = new Date(today);
        startOfWeek.setDate(today.getDate() - today.getDay());
        return { startDate: formatDate(startOfWeek), endDate: formatDate(today) };
      }
      case 'thisMonth': {
        const firstOfMonth = new Date(today.getFullYear(), today.getMonth(), 1);
        return { startDate: formatDate(firstOfMonth), endDate: formatDate(today) };
      }
      case 'lastMonth': {
        const firstOfLastMonth = new Date(today.getFullYear(), today.getMonth() - 1, 1);
        const lastOfLastMonth = new Date(today.getFullYear(), today.getMonth(), 0);
        return { startDate: formatDate(firstOfLastMonth), endDate: formatDate(lastOfLastMonth) };
      }
      case 'thisQuarter': {
        const quarterStart = new Date(today.getFullYear(), Math.floor(today.getMonth() / 3) * 3, 1);
        return { startDate: formatDate(quarterStart), endDate: formatDate(today) };
      }
      case 'ytd': {
        const yearStart = new Date(today.getFullYear(), 0, 1);
        return { startDate: formatDate(yearStart), endDate: formatDate(today) };
      }
      case 'lastYear': {
        const lastYearStart = new Date(today.getFullYear() - 1, 0, 1);
        const lastYearEnd = new Date(today.getFullYear() - 1, 11, 31);
        return { startDate: formatDate(lastYearStart), endDate: formatDate(lastYearEnd) };
      }
      case 'custom':
        return { startDate: customStartDate, endDate: customEndDate };
      default:
        return { startDate: formatDate(today), endDate: formatDate(today) };
    }
  };

  const fetchReport = async () => {
    try {
      setLoading(true);
      const { startDate, endDate } = getDateRange();
      
      if (!startDate || !endDate) {
        toast.error('Please select valid dates');
        setLoading(false);
        return;
      }

      const params = { startDate, endDate };
      
      if (useBusinessHours) {
        params.startTime = startTime;
        params.endTime = endTime;
      }

      // Add game filter for applicable reports
      if (selectedGames.length > 0 && ['player-activity', 'player-retention', 'hourly-heatmap'].includes(activeTab)) {
        params.games = selectedGames.join(',');
      }

      let endpoint = '/admin/reports/table-activity';
      
      switch (activeTab) {
        case 'player-activity':
          endpoint = '/admin/reports/player-activity';
          params.limit = playerLimit;
          break;
        case 'player-xp':
          endpoint = '/admin/reports/player-xp';
          break;
        case 'game-performance':
          endpoint = '/admin/reports/game-performance';
          break;
        case 'player-retention':
          endpoint = '/admin/reports/player-retention';
          break;
        case 'hourly-heatmap':
          endpoint = '/admin/reports/hourly-heatmap';
          break;
        case 'wsop-leaderboard':
          endpoint = '/admin/reports/wsop-leaderboard';
          break;
        case 'wpt-monthly':
          endpoint = '/admin/reports/wpt-monthly';
          break;
        default:
          endpoint = '/admin/reports/table-activity';
      }

      const response = await api.get(endpoint, { params });
      setReportData(response.data);
      
      // Extract available games from game-performance data
      if (activeTab === 'game-performance' && response.data?.games) {
        setAvailableGames(response.data.games.map(g => g.gameName));
      }
    } catch (error) {
      console.error('Error fetching report:', error);
      toast.error(error.response?.data?.error || 'Failed to load report');
    } finally {
      setLoading(false);
    }
  };

  // Fetch available games for the filter
  const fetchAvailableGames = async () => {
    try {
      const { startDate, endDate } = getDateRange();
      if (!startDate || !endDate) return;
      
      const params = { startDate, endDate };
      if (useBusinessHours) {
        params.startTime = startTime;
        params.endTime = endTime;
      }
      
      const response = await api.get('/admin/reports/game-performance', { params });
      if (response.data?.games) {
        setAvailableGames(response.data.games.map(g => g.gameName));
      }
    } catch (error) {
      console.error('Error fetching available games:', error);
    }
  };

  useEffect(() => {
    if (datePreset === 'custom' && (!customStartDate || !customEndDate)) {
      return;
    }
    fetchReport();
    // Also fetch available games when date changes
    fetchAvailableGames();
  }, [datePreset, customStartDate, customEndDate, useBusinessHours, startTime, endTime, activeTab, playerLimit, selectedGames]);

  // Fetch players for a specific table
  const fetchTablePlayers = async (table) => {
    try {
      setTablePlayersLoading(true);
      const { startDate, endDate } = getDateRange();
      
      const params = {
        tableName: table.tableName,
        // Use originalGameName for API query if available (for table-game mapping overrides)
        gameName: table.originalGameName || table.gameName,
        startDate,
        endDate,
      };
      
      if (useBusinessHours) {
        params.startTime = startTime;
        params.endTime = endTime;
      }
      
      const response = await api.get('/admin/reports/table-players', { params });
      setTablePlayersData(response.data);
    } catch (error) {
      console.error('Error fetching table players:', error);
      toast.error(error.response?.data?.error || 'Failed to load table players');
    } finally {
      setTablePlayersLoading(false);
    }
  };

  // Handle table click to show players
  const handleTableClick = (table) => {
    setSelectedTable(table);
    fetchTablePlayers(table);
  };

  // Handle player click to show stats (PlayerDetailModal fetches its own stats by playerId)
  const handlePlayerClick = (player) => {
    setSelectedPlayer(player);
  };

  // Sort handlers for tables
  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('desc');
    }
  };

  const formatDateDisplay = (dateStr) => {
    if (!dateStr) return '';
    const datePart = dateStr.split('T')[0];
    const [year, month, day] = datePart.split('-').map(Number);
    const date = new Date(year, month - 1, day);
    return date.toLocaleDateString('en-US', {
      weekday: 'short',
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  };

  const formatHours = (hours) => {
    if (!hours && hours !== 0) return '0h 0m';
    const h = Math.floor(hours);
    const m = Math.round((hours - h) * 60);
    return `${h}h ${m}m`;
  };

  const SortHeader = ({ field, label }) => (
    <th
      className="px-6 py-4 text-left text-sm font-semibold text-gray-300 cursor-pointer hover:text-white transition-colors"
      onClick={() => handleSort(field)}
    >
      <div className="flex items-center gap-1">
        {label}
        <ArrowUpDown size={14} className={sortField === field ? 'text-amber-500' : 'opacity-50'} />
      </div>
    </th>
  );

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-white">Reports</h1>
          <p className="text-gray-400 mt-1">Comprehensive analytics and insights</p>
        </div>
        <button
          onClick={fetchReport}
          disabled={loading}
          className="flex items-center gap-2 px-4 py-2 bg-amber-500 text-black font-medium rounded-lg hover:bg-amber-400 transition-colors disabled:opacity-50"
        >
          <RefreshCw size={18} className={loading ? 'animate-spin' : ''} />
          Refresh
        </button>
      </div>

      {/* Report Tabs */}
      <div className="flex gap-2 overflow-x-auto pb-2">
        {REPORT_TABS.map((tab) => (
          <button
            key={tab.id}
            onClick={() => {
              setActiveTab(tab.id);
              setSortField(tab.id === 'table-activity' ? 'totalActiveSeconds' : 'totalHours');
            }}
            className={`flex items-center gap-2 px-4 py-2.5 rounded-lg font-medium whitespace-nowrap transition-colors ${
              activeTab === tab.id
                ? 'bg-amber-500 text-black'
                : 'bg-gray-800 text-gray-300 hover:bg-gray-700'
            }`}
          >
            <tab.icon size={18} />
            {tab.label}
          </button>
        ))}
      </div>

      {/* Date Filters - Hide for lapsed-players, wsop-leaderboard, wpt-monthly, and discounts which have their own filters */}
      {activeTab !== 'lapsed-players' && activeTab !== 'wsop-leaderboard' && activeTab !== 'wpt-monthly' && activeTab !== 'discounts' && activeTab !== 'reward-lift' && activeTab !== 'free-play-month' && activeTab !== 'geofence-entries' && (
      <div className="bg-gray-800 rounded-xl p-6">
        <h2 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
          <Calendar className="text-amber-500" size={20} />
          Date Range
        </h2>
        <div className="flex flex-wrap gap-3">
          {DATE_PRESETS.map((preset) => (
            <button
              key={preset.value}
              onClick={() => setDatePreset(preset.value)}
              className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                datePreset === preset.value
                  ? 'bg-amber-500 text-black'
                  : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
              }`}
            >
              {preset.label}
            </button>
          ))}
        </div>

        {datePreset === 'custom' && (
          <div className="flex items-center gap-4 mt-4">
            <div>
              <label className="block text-sm text-gray-400 mb-1">Start Date</label>
              <input
                type="date"
                value={customStartDate}
                onChange={(e) => setCustomStartDate(e.target.value)}
                className="px-4 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white focus:outline-none focus:border-amber-500"
              />
            </div>
            <div>
              <label className="block text-sm text-gray-400 mb-1">End Date</label>
              <input
                type="date"
                value={customEndDate}
                onChange={(e) => setCustomEndDate(e.target.value)}
                className="px-4 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white focus:outline-none focus:border-amber-500"
              />
            </div>
          </div>
        )}

        {reportData && (
          <div className="mt-4 text-sm text-gray-400">
            Showing data from <span className="text-white font-medium">{formatDateDisplay(reportData.startDate)}</span>
            {reportData.startDate !== reportData.endDate && (
              <> to <span className="text-white font-medium">{formatDateDisplay(reportData.endDate)}</span></>
            )}
          </div>
        )}

        {/* Time Filter */}
        <div className="mt-6 pt-6 border-t border-gray-700">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-sm font-semibold text-white flex items-center gap-2">
              <Clock className="text-amber-500" size={16} />
              Business Hours Filter
            </h3>
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={useBusinessHours}
                onChange={(e) => setUseBusinessHours(e.target.checked)}
                className="w-4 h-4 rounded border-gray-600 bg-gray-700 text-amber-500 focus:ring-amber-500"
              />
              <span className="text-sm text-gray-400">Filter by time</span>
            </label>
          </div>
          
          {useBusinessHours && (
            <div className="flex items-center gap-4">
              <div>
                <label className="block text-xs text-gray-500 mb-1">Start Time</label>
                <input
                  type="time"
                  value={startTime}
                  onChange={(e) => setStartTime(e.target.value)}
                  className="px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white text-sm focus:outline-none focus:border-amber-500"
                />
              </div>
              <div className="text-gray-500 pt-5">to</div>
              <div>
                <label className="block text-xs text-gray-500 mb-1">End Time</label>
                <input
                  type="time"
                  value={endTime}
                  onChange={(e) => setEndTime(e.target.value)}
                  className="px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white text-sm focus:outline-none focus:border-amber-500"
                />
              </div>
            </div>
          )}
        </div>

        {/* Player Limit (for player-activity tab) */}
        {activeTab === 'player-activity' && (
          <div className="mt-6 pt-6 border-t border-gray-700">
            <div className="flex items-center gap-4">
              <label className="text-sm text-gray-400">Show top</label>
              <select
                value={playerLimit}
                onChange={(e) => setPlayerLimit(Number(e.target.value))}
                className="px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white text-sm focus:outline-none focus:border-amber-500"
              >
                <option value={10}>10 players</option>
                <option value={25}>25 players</option>
                <option value={50}>50 players</option>
                <option value={100}>100 players</option>
                <option value={100000}>All players</option>
              </select>
            </div>
          </div>
        )}

        {/* Game Filter (for player-activity, player-retention, hourly-heatmap tabs) */}
        {['player-activity', 'player-retention', 'hourly-heatmap'].includes(activeTab) && availableGames.length > 0 && (
          <div className="mt-6 pt-6 border-t border-gray-700">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-sm font-semibold text-white flex items-center gap-2">
                <Gamepad2 className="text-amber-500" size={16} />
                Game Filter
              </h3>
              {selectedGames.length > 0 && (
                <button
                  onClick={() => setSelectedGames([])}
                  className="text-xs text-amber-500 hover:text-amber-400"
                >
                  Clear All
                </button>
              )}
            </div>
            <div className="flex flex-wrap gap-2">
              {availableGames.map((game) => (
                <button
                  key={game}
                  onClick={() => {
                    if (selectedGames.includes(game)) {
                      setSelectedGames(selectedGames.filter(g => g !== game));
                    } else {
                      setSelectedGames([...selectedGames, game]);
                    }
                  }}
                  className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                    selectedGames.includes(game)
                      ? 'bg-amber-500 text-black'
                      : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
                  }`}
                >
                  {game}
                </button>
              ))}
            </div>
            {selectedGames.length > 0 && (
              <p className="mt-2 text-xs text-gray-500">
                Filtering by {selectedGames.length} game{selectedGames.length > 1 ? 's' : ''}
              </p>
            )}
          </div>
        )}
      </div>
      )}

      {/* Report Content */}
      {activeTab === 'table-activity' && (
        <TableActivityReport 
          data={reportData} 
          loading={loading} 
          sortField={sortField}
          sortDirection={sortDirection}
          onSort={handleSort}
          formatDateDisplay={formatDateDisplay}
          onTableClick={handleTableClick}
        />
      )}

      {activeTab === 'player-activity' && (
        <PlayerActivityReport
          data={reportData}
          loading={loading}
          formatHours={formatHours}
          dateRange={getDateRange()}
          onPlayerClick={handlePlayerClick}
        />
      )}

      {activeTab === 'player-xp' && (
        <PlayerXPReport 
          data={reportData} 
          loading={loading}
          formatHours={formatHours}
          onRefresh={fetchReport}
        />
      )}

      {activeTab === 'game-performance' && (
        <GamePerformanceReport 
          data={reportData} 
          loading={loading}
          formatHours={formatHours}
        />
      )}

      {activeTab === 'player-retention' && (
        <PlayerRetentionReport 
          data={reportData} 
          loading={loading}
          formatDateDisplay={formatDateDisplay}
        />
      )}

      {activeTab === 'lapsed-players' && (
        <LapsedPlayersReport 
          formatHours={formatHours}
        />
      )}

      {/* Road to WSOP + WPT Monthly moved to the Leaderboards page */}

      {activeTab === 'hourly-heatmap' && (
        <HourlyHeatmapReport 
          data={reportData} 
          loading={loading}
        />
      )}

      {activeTab === 'discounts' && (
        <DiscountReport />
      )}

      {activeTab === 'reward-lift' && (
        <RewardLift />
      )}

      {activeTab === 'free-play-month' && (
        <FreePlayByMonth />
      )}

      {activeTab === 'geofence-entries' && (
        <GeofenceReport />
      )}

      {/* Table Players Modal */}
      {selectedTable && (
        <TablePlayersModal
          visible={!!selectedTable}
          onClose={() => setSelectedTable(null)}
          table={selectedTable}
          data={tablePlayersData}
          loading={tablePlayersLoading}
          onPlayerClick={handlePlayerClick}
          formatHours={formatHours}
        />
      )}

      {/* Player Detail Modal (shared) */}
      <PlayerDetailModal
        visible={!!selectedPlayer}
        onClose={() => setSelectedPlayer(null)}
        playerId={selectedPlayer?.playerId}
        player={selectedPlayer}
      />
    </div>
  );
}


// ==========================================
// TABLE ACTIVITY REPORT
// ==========================================
function TableActivityReport({ data, loading, sortField, sortDirection, onSort, formatDateDisplay, onTableClick }) {
  const [groupByGame, setGroupByGame] = useState(true);
  const [expandedGames, setExpandedGames] = useState({});

  const sortedTables = useMemo(() => {
    if (!data?.tables) return [];
    
    return [...data.tables].sort((a, b) => {
      let aVal = a[sortField];
      let bVal = b[sortField];
      
      if (typeof aVal === 'string' && typeof bVal === 'string') {
        return sortDirection === 'asc' 
          ? aVal.localeCompare(bVal)
          : bVal.localeCompare(aVal);
      }
      
      return sortDirection === 'asc' ? aVal - bVal : bVal - aVal;
    });
  }, [data?.tables, sortField, sortDirection]);

  const formatSeconds = (seconds) => {
    const hours = Math.floor(seconds / 3600);
    const mins = Math.floor((seconds % 3600) / 60);
    return `${hours}h ${mins}m`;
  };

  // Group tables by game name, then sum duplicate table numbers within each game
  const groupedByGame = useMemo(() => {
    if (!sortedTables.length) return {};
    
    const groups = {};
    sortedTables.forEach(table => {
      const gameName = table.gameName || 'Unknown Game';
      if (!groups[gameName]) {
        groups[gameName] = {
          tableMap: {}, // Map of tableName -> aggregated data
          totalActiveSeconds: 0,
          totalSessions: 0,
          totalUniquePlayers: 0,
        };
      }
      
      const tableName = table.tableName;
      if (!groups[gameName].tableMap[tableName]) {
        groups[gameName].tableMap[tableName] = {
          tableName,
          gameName,
          originalGameName: table.originalGameName || table.gameName, // Preserve original for API calls
          totalActiveSeconds: 0,
          sessionCount: 0,
          uniquePlayerCount: 0,
        };
      }
      
      // Sum the values for this table
      groups[gameName].tableMap[tableName].totalActiveSeconds += table.totalActiveSeconds || 0;
      groups[gameName].tableMap[tableName].sessionCount += table.sessionCount || 0;
      groups[gameName].tableMap[tableName].uniquePlayerCount += table.uniquePlayerCount || 0;
      
      // Update game totals
      groups[gameName].totalActiveSeconds += table.totalActiveSeconds || 0;
      groups[gameName].totalSessions += table.sessionCount || 0;
      groups[gameName].totalUniquePlayers += table.uniquePlayerCount || 0;
    });
    
    // Convert tableMap to array and add formatted time
    Object.keys(groups).forEach(gameName => {
      groups[gameName].tables = Object.values(groups[gameName].tableMap).map(t => ({
        ...t,
        totalActiveTime: formatSeconds(t.totalActiveSeconds),
      }));
      delete groups[gameName].tableMap;
    });
    
    return groups;
  }, [sortedTables]);

  const toggleGameExpanded = (gameName) => {
    setExpandedGames(prev => ({
      ...prev,
      [gameName]: !prev[gameName]
    }));
  };

  // Check if data is grouped by table (no activityDate field)
  const isGrouped = data?.groupByTable !== false && sortedTables.length > 0 && !sortedTables[0]?.activityDate;

  const exportToCSV = () => {
    if (!sortedTables.length) return;

    const headers = isGrouped 
      ? ['Table', 'Game', 'Active Time', 'Sessions', 'Unique Players', 'Total Seconds']
      : ['Date', 'Table', 'Game', 'Active Time', 'Sessions', 'Unique Players', 'Total Seconds'];
    const rows = sortedTables.map(t => isGrouped 
      ? [t.tableName, t.gameName, t.totalActiveTime, t.sessionCount, t.uniquePlayerCount, t.totalActiveSeconds]
      : [formatDateDisplay(t.activityDate), t.tableName, t.gameName, t.totalActiveTime, t.sessionCount, t.uniquePlayerCount, t.totalActiveSeconds]
    );

    const csvContent = [
      headers.join(','),
      ...rows.map(row => row.map(cell => `"${cell}"`).join(','))
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `table-activity-report-${data?.startDate}-${data?.endDate}.csv`;
    link.click();
  };

  const SortHeader = ({ field, label }) => (
    <th
      className="px-6 py-4 text-left text-sm font-semibold text-gray-300 cursor-pointer hover:text-white transition-colors"
      onClick={() => onSort(field)}
    >
      <div className="flex items-center gap-1">
        {label}
        <ArrowUpDown size={14} className={sortField === field ? 'text-amber-500' : 'opacity-50'} />
      </div>
    </th>
  );

  return (
    <>
      {/* Summary Stats */}
      {data?.summary && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div className="bg-gray-800 rounded-xl p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-amber-500/20 rounded-lg flex items-center justify-center">
                <Table2 className="w-5 h-5 text-amber-500" />
              </div>
              <div>
                <div className="text-2xl font-bold text-white">{data.tableCount}</div>
                <div className="text-sm text-gray-400">Active Tables</div>
              </div>
            </div>
          </div>
          <div className="bg-gray-800 rounded-xl p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-green-500/20 rounded-lg flex items-center justify-center">
                <Clock className="w-5 h-5 text-green-500" />
              </div>
              <div>
                <div className="text-2xl font-bold text-white">{data.summary.totalActiveTime}</div>
                <div className="text-sm text-gray-400">Total Active Time</div>
              </div>
            </div>
          </div>
          <div className="bg-gray-800 rounded-xl p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-blue-500/20 rounded-lg flex items-center justify-center">
                <BarChart3 className="w-5 h-5 text-blue-500" />
              </div>
              <div>
                <div className="text-2xl font-bold text-white">{data.summary.totalSessions}</div>
                <div className="text-sm text-gray-400">Total Sessions</div>
              </div>
            </div>
          </div>
          <div className="bg-gray-800 rounded-xl p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-purple-500/20 rounded-lg flex items-center justify-center">
                <Clock className="w-5 h-5 text-purple-500" />
              </div>
              <div>
                <div className="text-2xl font-bold text-white">{data.summary.averageTableTime}</div>
                <div className="text-sm text-gray-400">Avg Time/Table</div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Table */}
      <div className="bg-gray-800 rounded-xl overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-700 flex items-center justify-between">
          <div>
            <h2 className="text-lg font-semibold text-white flex items-center gap-2">
              <FileText className="text-amber-500" size={20} />
              Tables Breakdown
            </h2>
            <p className="text-sm text-gray-400 mt-1">
              {isGrouped 
                ? 'Click on a table to see player details â€¢ Time each table had at least one player seated'
                : 'Time each table had at least one player seated'
              }
            </p>
          </div>
          <div className="flex items-center gap-3">
            {/* Group by game toggle */}
            <label className="flex items-center gap-2 text-sm text-gray-400 cursor-pointer">
              <input
                type="checkbox"
                checked={groupByGame}
                onChange={(e) => setGroupByGame(e.target.checked)}
                className="w-4 h-4 rounded border-gray-600 bg-gray-700 text-amber-500 focus:ring-amber-500"
              />
              Group by Game
            </label>
            <button
              onClick={exportToCSV}
              disabled={!sortedTables.length}
              className="flex items-center gap-2 px-4 py-2 bg-gray-700 text-white rounded-lg hover:bg-gray-600 transition-colors disabled:opacity-50"
            >
              <Download size={18} />
              Export CSV
            </button>
          </div>
        </div>

        {loading ? (
          <div className="flex flex-col items-center justify-center py-16">
            <RefreshCw className="w-8 h-8 text-amber-500 animate-spin mb-4" />
            <p className="text-gray-400">Loading report data...</p>
          </div>
        ) : !sortedTables.length ? (
          <div className="flex flex-col items-center justify-center py-16">
            <Table2 className="w-12 h-12 text-gray-600 mb-3" />
            <p className="text-gray-400">No table activity found for this date range</p>
          </div>
        ) : groupByGame ? (
          /* Grouped by Game View */
          <div className="divide-y divide-gray-700">
            {Object.entries(groupedByGame)
              .sort(([a], [b]) => a.localeCompare(b))
              .map(([gameName, gameData]) => (
                <div key={gameName}>
                  {/* Game Header - Clickable */}
                  <button
                    onClick={() => toggleGameExpanded(gameName)}
                    className="w-full px-6 py-4 flex items-center justify-between hover:bg-gray-750 transition-colors"
                  >
                    <div className="flex items-center gap-3">
                      <ChevronRight 
                        size={20} 
                        className={`text-gray-500 transition-transform ${expandedGames[gameName] ? 'rotate-90' : ''}`}
                      />
                      <Gamepad2 size={20} className="text-amber-500" />
                      <span className="font-semibold text-white">{gameName}</span>
                      <span className="text-gray-500 text-sm">
                        ({gameData.tables.length} table{gameData.tables.length !== 1 ? 's' : ''})
                      </span>
                    </div>
                    <div className="flex items-center gap-6 text-sm">
                      <div className="flex items-center gap-2">
                        <Clock size={14} className="text-green-500" />
                        <span className="text-gray-300 font-mono">{formatSeconds(gameData.totalActiveSeconds)}</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <BarChart3 size={14} className="text-blue-500" />
                        <span className="text-gray-300">{gameData.totalSessions} sessions</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <Users size={14} className="text-purple-500" />
                        <span className="text-gray-300">{gameData.totalUniquePlayers} players</span>
                      </div>
                    </div>
                  </button>

                  {/* Expanded Table List */}
                  {expandedGames[gameName] && (
                    <div className="bg-gray-850 border-t border-gray-700">
                      <table className="w-full">
                        <thead className="bg-gray-700/50">
                          <tr>
                            {!isGrouped && <th className="px-6 py-3 text-left text-xs font-semibold text-gray-400">Date</th>}
                            <th className="px-6 py-3 text-left text-xs font-semibold text-gray-400">Table</th>
                            <th className="px-6 py-3 text-left text-xs font-semibold text-gray-400">Active Time</th>
                            <th className="px-6 py-3 text-left text-xs font-semibold text-gray-400">Sessions</th>
                            <th className="px-6 py-3 text-left text-xs font-semibold text-gray-400">Unique Players</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-700/50">
                          {gameData.tables
                            .sort((a, b) => a.tableName.localeCompare(b.tableName, undefined, { numeric: true }))
                            .map((table, index) => (
                              <tr 
                                key={`${table.tableName}-${table.activityDate || index}`}
                                className={`hover:bg-gray-700/50 ${onTableClick ? 'cursor-pointer' : ''}`}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  onTableClick && onTableClick(table);
                                }}
                              >
                                {!isGrouped && (
                                  <td className="px-6 py-3 text-sm text-gray-400">
                                    {formatDateDisplay(table.activityDate)}
                                  </td>
                                )}
                                <td className="px-6 py-3">
                                  <span className="text-white">Table {table.tableName}</span>
                                </td>
                                <td className="px-6 py-3">
                                  <span className="font-mono text-green-400">{table.totalActiveTime}</span>
                                </td>
                                <td className="px-6 py-3 text-gray-300">{table.sessionCount}</td>
                                <td className="px-6 py-3 text-gray-300">{table.uniquePlayerCount}</td>
                              </tr>
                            ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              ))}
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-700">
                <tr>
                  {!isGrouped && <SortHeader field="activityDate" label="Date" />}
                  <SortHeader field="tableName" label="Table" />
                  <SortHeader field="gameName" label="Game" />
                  <SortHeader field="totalActiveSeconds" label="Active Time" />
                  <SortHeader field="sessionCount" label="Sessions" />
                  <SortHeader field="uniquePlayerCount" label="Unique Players" />
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-700">
                {sortedTables.map((table, index) => (
                  <tr 
                    key={`${table.tableName}-${table.gameName}-${table.activityDate || index}`} 
                    className={`hover:bg-gray-750 ${onTableClick ? 'cursor-pointer' : ''}`}
                    onClick={() => onTableClick && onTableClick(table)}
                  >
                    {!isGrouped && (
                      <td className="px-6 py-4">
                        <div className="flex items-center gap-2 text-gray-300">
                          <Calendar size={16} className="text-gray-500" />
                          {formatDateDisplay(table.activityDate)}
                        </div>
                      </td>
                    )}
                    <td className="px-6 py-4">
                      <span className="font-medium text-white">Table {table.tableName}</span>
                    </td>
                    <td className="px-6 py-4">
                      <span className="text-amber-500">{table.gameName}</span>
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-2">
                        <Clock size={16} className="text-green-500" />
                        <span className="font-mono text-white">{table.totalActiveTime}</span>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <span className="text-gray-300">{table.sessionCount}</span>
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-2">
                        <Users size={16} className="text-blue-400" />
                        <span className="text-gray-300">{table.uniquePlayerCount}</span>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </>
  );
}


// ==========================================
// PLAYER ACTIVITY REPORT (Top Players)
// ==========================================
function PlayerActivityReport({ data, loading, formatHours, dateRange, onPlayerClick }) {
  const [showConfig, setShowConfig] = useState(false);
  const [sortField, setSortField] = useState('valueScore');
  const [sortDirection, setSortDirection] = useState('desc');

  // Default algorithm config for Top Players value scoring
  const DEFAULT_TOP_PLAYERS_CONFIG = {
    // Value score weights (should add up to 100)
    hoursWeight: 65,
    avgWagerWeight: 30,
    visitsWeight: 5,
    // Value score scale
    maxHours: 100,   // hours to reach max score for hours component
    maxAvgWager: 200, // avg wager $ to reach max score for wager component
    maxVisits: 30,    // visits to reach max score for visits component
    // Comp thresholds (value score => comp amount)
    compTiers: [
      { minScore: 85, amount: 250, label: '$250' },
      { minScore: 70, amount: 200, label: '$200' },
      { minScore: 55, amount: 150, label: '$150' },
      { minScore: 40, amount: 100, label: '$100' },
      { minScore: 25, amount: 50, label: '$50' },
      { minScore: 10, amount: 25, label: '$25' },
    ],
  };

  // Load saved config from backend (falls back to localStorage then defaults)
  const loadSavedConfig = () => {
    try {
      const saved = localStorage.getItem('topplayers-algorithm-config');
      if (saved) {
        return { ...DEFAULT_TOP_PLAYERS_CONFIG, ...JSON.parse(saved) };
      }
    } catch (e) {
      console.error('Error loading saved Top Players config:', e);
    }
    return DEFAULT_TOP_PLAYERS_CONFIG;
  };

  const [config, setConfig] = useState(loadSavedConfig);

  // Load config from backend on mount
  useEffect(() => {
    const fetchConfig = async () => {
      try {
        const res = await api.get('/admin/settings/top-players-algorithm');
        if (res.data?.success && res.data?.config && !res.data?.isDefault) {
          const merged = { ...DEFAULT_TOP_PLAYERS_CONFIG, ...res.data.config };
          setConfig(merged);
          localStorage.setItem('topplayers-algorithm-config', JSON.stringify(merged));
        }
      } catch (e) {
        // Silently fall back to localStorage/defaults
        console.log('Could not load algorithm config from backend, using local');
      }
    };
    fetchConfig();
  }, []);

  // Save current config to backend AND localStorage
  const saveAsDefault = async () => {
    try {
      localStorage.setItem('topplayers-algorithm-config', JSON.stringify(config));
      await api.put('/admin/settings/top-players-algorithm', config);
      toast.success('Algorithm settings saved (synced to all devices)');
    } catch (e) {
      console.error('Error saving config:', e);
      toast.warning('Saved locally but failed to sync to server');
    }
  };

  // Reset to original defaults
  const resetToDefaults = () => {
    setConfig(DEFAULT_TOP_PLAYERS_CONFIG);
  };

  // Calculate value score client-side using configurable weights
  const calculateValueScore = (player) => {
    const hours = parseFloat(player.totalHours || 0);
    const avgWager = parseFloat(player.avgWager || player.avgBet || 0);
    const visits = parseInt(player.visits || player.sessionCount || 0);

    // Normalize each component to 0-1, then multiply by weight
    const hoursNorm = Math.min(1, hours / Math.max(1, config.maxHours));
    const wagerNorm = Math.min(1, avgWager / Math.max(1, config.maxAvgWager));
    const visitsNorm = Math.min(1, visits / Math.max(1, config.maxVisits));

    const totalWeight = config.hoursWeight + config.avgWagerWeight + config.visitsWeight;
    if (totalWeight === 0) return 1;

    // Weighted sum normalized to 1-99
    const raw = (hoursNorm * config.hoursWeight + wagerNorm * config.avgWagerWeight + visitsNorm * config.visitsWeight) / totalWeight;
    return Math.max(1, Math.min(99, Math.round(raw * 99)));
  };

  // Get recommended comp based on value score
  const getRecommendedComp = (valueScore) => {
    const tiers = [...config.compTiers].sort((a, b) => b.minScore - a.minScore);
    for (const tier of tiers) {
      if (valueScore >= tier.minScore) {
        return tier;
      }
    }
    return null;
  };

  // Get comp tier color
  const getCompColor = (amount) => {
    if (amount >= 250) return { text: 'text-purple-400', bg: 'bg-purple-500/20' };
    if (amount >= 200) return { text: 'text-blue-400', bg: 'bg-blue-500/20' };
    if (amount >= 150) return { text: 'text-green-400', bg: 'bg-green-500/20' };
    if (amount >= 100) return { text: 'text-yellow-400', bg: 'bg-yellow-500/20' };
    if (amount >= 50) return { text: 'text-orange-400', bg: 'bg-orange-500/20' };
    return { text: 'text-gray-400', bg: 'bg-gray-500/20' };
  };

  if (loading) {
    return (
      <div className="bg-gray-800 rounded-xl p-16 flex flex-col items-center justify-center">
        <RefreshCw className="w-8 h-8 text-amber-500 animate-spin mb-4" />
        <p className="text-gray-400">Loading player data...</p>
      </div>
    );
  }

  // Map API response - API returns topPlayersByHours, frontend expects players
  const rawPlayers = data?.topPlayersByHours || data?.players || [];
  const summary = data?.playerSummary || data?.summary || {};

  // Delegate to the shared canonical resolver (1=VIP .. 5=Chairman; keys; names).
  const normalizeTier = resolveTierName;

  // Enrich players with recalculated value scores and comp recommendations
  const enrichedPlayers = rawPlayers.map(player => {
    const valueScore = calculateValueScore(player);
    const comp = getRecommendedComp(valueScore);
    return {
      ...player,
      calculatedValueScore: valueScore,
      recommendedComp: comp,
    };
  });

  // Sort players
  const sortedPlayers = [...enrichedPlayers].sort((a, b) => {
    let aVal, bVal;
    switch (sortField) {
      case 'valueScore':
        aVal = a.calculatedValueScore || 0;
        bVal = b.calculatedValueScore || 0;
        break;
      case 'hours':
        aVal = parseFloat(a.totalHours || 0);
        bVal = parseFloat(b.totalHours || 0);
        break;
      case 'visits':
        aVal = parseInt(a.visits || a.sessionCount || 0);
        bVal = parseInt(b.visits || b.sessionCount || 0);
        break;
      case 'avgWager':
        aVal = parseFloat(a.avgWager || 0);
        bVal = parseFloat(b.avgWager || 0);
        break;
      case 'recency':
        aVal = a.daysSinceLastPlayed ?? 9999;
        bVal = b.daysSinceLastPlayed ?? 9999;
        break;
      case 'comp':
        aVal = a.recommendedComp?.amount || 0;
        bVal = b.recommendedComp?.amount || 0;
        break;
      default:
        aVal = parseFloat(a.totalHours || 0);
        bVal = parseFloat(b.totalHours || 0);
    }
    return sortDirection === 'desc' ? bVal - aVal : aVal - bVal;
  });

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(d => d === 'desc' ? 'asc' : 'desc');
    } else {
      setSortField(field);
      setSortDirection('desc');
    }
  };

  // Comp budget summary
  const compSummary = enrichedPlayers.reduce((acc, p) => {
    if (p.recommendedComp) {
      const key = p.recommendedComp.label;
      acc[key] = (acc[key] || 0) + 1;
      acc.totalBudget += p.recommendedComp.amount;
      acc.totalComped += 1;
    }
    return acc;
  }, { totalBudget: 0, totalComped: 0 });

  // CSV Export function
  const exportToCSV = () => {
    if (!sortedPlayers.length) return;

    const headers = ['Rank', 'Player Name', 'Member Number', 'Hours', 'Visits', 'Sessions', 'Avg Wager', 'Points', 'Value Score', 'Recommended Comp', 'Last Played', 'Days Since', 'Tier', 'App User'];
    const rows = sortedPlayers.map((player, index) => [
      index + 1,
      player.playerName || `${player.firstName || ''} ${player.lastName || ''}`.trim() || 'Unknown',
      player.memberNumber || '',
      player.totalHours || 0,
      player.visits || player.sessionCount || 0,
      player.sessionCount || 0,
      player.avgWager != null ? `$${player.avgWager}` : '-',
      player.totalPoints || 0,
      player.calculatedValueScore,
      player.recommendedComp?.label || 'None',
      player.lastPlayed ? formatBusinessDate(player.lastPlayed, { month: 'numeric', day: 'numeric', year: 'numeric' }) : '-',
      player.daysSinceLastPlayed ?? '-',
      normalizeTier(player.tier),
      player.isAppUser ? 'Yes' : 'No',
    ]);

    const csvContent = [
      headers.join(','),
      ...rows.map(row => row.map(cell => `"${cell}"`).join(','))
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `top-players-report-${dateRange?.startDate || 'export'}-${dateRange?.endDate || ''}.csv`;
    link.click();
  };

  if (!rawPlayers.length) {
    return (
      <div className="bg-gray-800 rounded-xl p-16 flex flex-col items-center justify-center">
        <Users className="w-12 h-12 text-gray-600 mb-3" />
        <p className="text-gray-400">No player activity found for this date range</p>
      </div>
    );
  }

  const weightTotal = config.hoursWeight + config.avgWagerWeight + config.visitsWeight;

  return (
    <>
      {/* Summary Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="bg-gray-800 rounded-xl p-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-amber-500/20 rounded-lg flex items-center justify-center">
              <Users className="w-5 h-5 text-amber-500" />
            </div>
            <div>
              <div className="text-2xl font-bold text-white">{(summary.totalUniquePlayers || rawPlayers.length).toLocaleString()}</div>
              <div className="text-sm text-gray-400">Unique Players</div>
            </div>
          </div>
        </div>
        <div className="bg-gray-800 rounded-xl p-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-green-500/20 rounded-lg flex items-center justify-center">
              <TrendingUp className="w-5 h-5 text-green-500" />
            </div>
            <div>
              <div className="text-2xl font-bold text-white">{(summary.newPlayers || 0).toLocaleString()}</div>
              <div className="text-sm text-gray-400">New Club Sign-Ups</div>
            </div>
          </div>
        </div>
        <div className="bg-gray-800 rounded-xl p-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-blue-500/20 rounded-lg flex items-center justify-center">
              <Users className="w-5 h-5 text-blue-500" />
            </div>
            <div>
              <div className="text-2xl font-bold text-white">{(summary.returningPlayers || 0).toLocaleString()}</div>
              <div className="text-sm text-gray-400">Existing Members</div>
            </div>
          </div>
        </div>
        <div className="bg-gray-800 rounded-xl p-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-purple-500/20 rounded-lg flex items-center justify-center">
              <Trophy className="w-5 h-5 text-purple-500" />
            </div>
            <div>
              <div className="text-2xl font-bold text-white">{(summary.newPlayerPercent || 0)}%</div>
              <div className="text-sm text-gray-400">New Sign-Up Rate</div>
            </div>
          </div>
        </div>
      </div>

      {/* Algorithm Settings Panel */}
      <div className="bg-gray-800 rounded-xl p-6">
        <div className="flex items-center justify-between">
          <h2 className="text-lg font-semibold text-white flex items-center gap-2">
            <Sliders className="text-amber-500" size={20} />
            Player Value Algorithm
          </h2>
          <div className="flex items-center gap-3">
            {compSummary.totalComped > 0 && (
              <span className="text-xs text-gray-400">
                {compSummary.totalComped} players â€¢ ${compSummary.totalBudget.toLocaleString()} total comp budget
              </span>
            )}
            <button
              onClick={() => setShowConfig(!showConfig)}
              className="flex items-center gap-2 px-3 py-1.5 bg-gray-700 text-gray-300 rounded-lg hover:bg-gray-600 transition-colors text-sm"
            >
              <Sliders size={14} />
              {showConfig ? 'Hide' : 'Show'} Algorithm Settings
            </button>
          </div>
        </div>

        {showConfig && (
          <div className="mt-6 pt-6 border-t border-gray-700 space-y-6">
            {/* Value Score Weights */}
            <div>
              <h3 className="text-sm font-semibold text-white mb-3 flex items-center gap-2">
                <BarChart3 size={16} className="text-amber-500" />
                Value Score Weights
                <span className={`text-xs ml-2 px-2 py-0.5 rounded ${weightTotal === 100 ? 'bg-green-500/20 text-green-400' : 'bg-red-500/20 text-red-400'}`}>
                  Total: {weightTotal}%{weightTotal !== 100 ? ' (should be 100%)' : ' âœ“'}
                </span>
              </h3>
              <div className="grid grid-cols-3 gap-6">
                <div>
                  <label className="block text-xs text-gray-400 mb-1">Hours Played ({config.hoursWeight}%)</label>
                  <input
                    type="range"
                    min="0"
                    max="100"
                    value={config.hoursWeight}
                    onChange={(e) => setConfig({ ...config, hoursWeight: parseInt(e.target.value) })}
                    className="w-full accent-amber-500"
                  />
                  <div className="flex justify-between text-xs text-gray-500 mt-1">
                    <span>0%</span>
                    <span>100%</span>
                  </div>
                </div>
                <div>
                  <label className="block text-xs text-gray-400 mb-1">Avg Wager ({config.avgWagerWeight}%)</label>
                  <input
                    type="range"
                    min="0"
                    max="100"
                    value={config.avgWagerWeight}
                    onChange={(e) => setConfig({ ...config, avgWagerWeight: parseInt(e.target.value) })}
                    className="w-full accent-green-500"
                  />
                  <div className="flex justify-between text-xs text-gray-500 mt-1">
                    <span>0%</span>
                    <span>100%</span>
                  </div>
                </div>
                <div>
                  <label className="block text-xs text-gray-400 mb-1">Visits ({config.visitsWeight}%)</label>
                  <input
                    type="range"
                    min="0"
                    max="100"
                    value={config.visitsWeight}
                    onChange={(e) => setConfig({ ...config, visitsWeight: parseInt(e.target.value) })}
                    className="w-full accent-blue-500"
                  />
                  <div className="flex justify-between text-xs text-gray-500 mt-1">
                    <span>0%</span>
                    <span>100%</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Scaling Maximums */}
            <div>
              <h3 className="text-sm font-semibold text-white mb-3 flex items-center gap-2">
                <TrendingUp size={16} className="text-amber-500" />
                Score Scaling (values that earn max points)
              </h3>
              <div className="grid grid-cols-3 gap-6">
                <div>
                  <label className="block text-xs text-gray-400 mb-1">Max Hours ({config.maxHours}h)</label>
                  <input
                    type="range"
                    min="10"
                    max="500"
                    step="10"
                    value={config.maxHours}
                    onChange={(e) => setConfig({ ...config, maxHours: parseInt(e.target.value) })}
                    className="w-full accent-amber-500"
                  />
                  <div className="flex justify-between text-xs text-gray-500 mt-1">
                    <span>10h</span>
                    <span>500h</span>
                  </div>
                </div>
                <div>
                  <label className="block text-xs text-gray-400 mb-1">Max Avg Wager (${config.maxAvgWager})</label>
                  <input
                    type="range"
                    min="10"
                    max="1000"
                    step="10"
                    value={config.maxAvgWager}
                    onChange={(e) => setConfig({ ...config, maxAvgWager: parseInt(e.target.value) })}
                    className="w-full accent-green-500"
                  />
                  <div className="flex justify-between text-xs text-gray-500 mt-1">
                    <span>$10</span>
                    <span>$1000</span>
                  </div>
                </div>
                <div>
                  <label className="block text-xs text-gray-400 mb-1">Max Visits ({config.maxVisits})</label>
                  <input
                    type="range"
                    min="5"
                    max="100"
                    step="5"
                    value={config.maxVisits}
                    onChange={(e) => setConfig({ ...config, maxVisits: parseInt(e.target.value) })}
                    className="w-full accent-blue-500"
                  />
                  <div className="flex justify-between text-xs text-gray-500 mt-1">
                    <span>5</span>
                    <span>100</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Comp Thresholds */}
            <div>
              <h3 className="text-sm font-semibold text-white mb-3 flex items-center gap-2">
                <DollarSign size={16} className="text-amber-500" />
                Recommended Comp Thresholds (Value Score)
              </h3>
              <div className="grid grid-cols-6 gap-4">
                {config.compTiers.map((tier, i) => (
                  <div key={i}>
                    <label className="block text-xs text-gray-400 mb-1">{tier.label} Comp (â‰¥ {tier.minScore})</label>
                    <input
                      type="range"
                      min="1"
                      max="99"
                      value={tier.minScore}
                      onChange={(e) => {
                        const newTiers = [...config.compTiers];
                        newTiers[i] = { ...newTiers[i], minScore: parseInt(e.target.value) };
                        setConfig({ ...config, compTiers: newTiers });
                      }}
                      className={`w-full ${i === 0 ? 'accent-purple-500' : i === 1 ? 'accent-blue-500' : i === 2 ? 'accent-green-500' : i === 3 ? 'accent-yellow-500' : i === 4 ? 'accent-orange-500' : 'accent-gray-500'}`}
                    />
                  </div>
                ))}
              </div>
            </div>

            {/* Action Buttons */}
            <div className="flex justify-end gap-4">
              <button
                onClick={resetToDefaults}
                className="text-sm text-gray-400 hover:text-white transition-colors"
              >
                Reset to Defaults
              </button>
              <button
                onClick={saveAsDefault}
                className="flex items-center gap-1 text-sm text-amber-500 hover:text-amber-400 transition-colors"
              >
                <Sliders size={14} />
                Save as Default
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Top Players Table */}
      <div className="bg-gray-800 rounded-xl overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-700 flex items-center justify-between">
          <div>
            <h2 className="text-lg font-semibold text-white flex items-center gap-2">
              <Trophy className="text-amber-500" size={20} />
              Top Players
            </h2>
            <p className="text-sm text-gray-400 mt-1">
              Players ranked by value score with engagement metrics â€¢ Click column headers to sort
            </p>
          </div>
          <button
            onClick={exportToCSV}
            disabled={!sortedPlayers.length}
            className="flex items-center gap-2 px-4 py-2 bg-gray-700 text-white rounded-lg hover:bg-gray-600 transition-colors disabled:opacity-50"
          >
            <Download size={18} />
            Export CSV
          </button>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-700">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-300">Rank</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-300">Player</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-300">Member #</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-300 cursor-pointer hover:text-white" onClick={() => handleSort('hours')}>
                  <span className="flex items-center gap-1">Hours <ArrowUpDown size={12} className={sortField === 'hours' ? 'text-amber-500' : 'opacity-50'} /></span>
                </th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-300 cursor-pointer hover:text-white" onClick={() => handleSort('visits')}>
                  <span className="flex items-center gap-1">Visits <ArrowUpDown size={12} className={sortField === 'visits' ? 'text-amber-500' : 'opacity-50'} /></span>
                </th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-300 cursor-pointer hover:text-white" onClick={() => handleSort('avgWager')}>
                  <span className="flex items-center gap-1">Avg Wager <ArrowUpDown size={12} className={sortField === 'avgWager' ? 'text-amber-500' : 'opacity-50'} /></span>
                </th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-300 cursor-pointer hover:text-white" onClick={() => handleSort('valueScore')}>
                  <span className="flex items-center gap-1">Value <ArrowUpDown size={12} className={sortField === 'valueScore' ? 'text-amber-500' : 'opacity-50'} /></span>
                </th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-300 cursor-pointer hover:text-white" onClick={() => handleSort('comp')}>
                  <span className="flex items-center gap-1">Rec. Comp <ArrowUpDown size={12} className={sortField === 'comp' ? 'text-amber-500' : 'opacity-50'} /></span>
                </th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-300 cursor-pointer hover:text-white" onClick={() => handleSort('recency')}>
                  <span className="flex items-center gap-1">Recency <ArrowUpDown size={12} className={sortField === 'recency' ? 'text-amber-500' : 'opacity-50'} /></span>
                </th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-300">Tier</th>
                <th className="px-4 py-3 text-center text-xs font-semibold text-gray-300">App</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-700">
              {sortedPlayers.map((player, index) => {
                const valueScore = player.calculatedValueScore;
                const valueColor = valueScore >= 75 ? 'text-green-400' : valueScore >= 50 ? 'text-amber-400' : valueScore >= 25 ? 'text-orange-400' : 'text-red-400';
                const recencyDays = player.daysSinceLastPlayed;
                const recencyColor = recencyDays == null ? 'text-gray-500' : recencyDays <= 7 ? 'text-green-400' : recencyDays <= 30 ? 'text-amber-400' : recencyDays <= 90 ? 'text-orange-400' : 'text-red-400';
                const recencyLabel = recencyDays == null ? '-' : recencyDays === 0 ? 'Today' : recencyDays === 1 ? '1 day ago' : `${recencyDays}d ago`;
                const comp = player.recommendedComp;
                const compColor = comp ? getCompColor(comp.amount) : null;

                return (
                <tr
                  key={player.playerId || index}
                  onClick={() => onPlayerClick && onPlayerClick(player)}
                  className="hover:bg-gray-750 cursor-pointer"
                >
                  <td className="px-4 py-3">
                    <div className={`w-7 h-7 rounded-full flex items-center justify-center text-sm font-bold ${
                      index === 0 ? 'bg-yellow-500 text-black' :
                      index === 1 ? 'bg-gray-400 text-black' :
                      index === 2 ? 'bg-amber-700 text-white' :
                      'bg-gray-700 text-gray-300'
                    }`}>
                      {index + 1}
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    <span className="font-medium text-white text-sm">
                      {player.playerName || `${player.firstName || ''} ${player.lastName || ''}`.trim() || 'Unknown'}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    <span className="text-gray-400 font-mono text-xs">{player.memberNumber || '-'}</span>
                  </td>
                  <td className="px-4 py-3">
                    <span className="font-mono text-white text-sm">{formatHours(player.totalHours)}</span>
                  </td>
                  <td className="px-4 py-3">
                    <span className="text-gray-300 text-sm">{player.visits ?? player.sessionCount ?? '-'}</span>
                  </td>
                  <td className="px-4 py-3">
                    <span className="text-green-400 text-sm font-medium">{player.avgWager != null ? `$${player.avgWager.toLocaleString()}` : '-'}</span>
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-1.5">
                      <div className="w-12 h-1.5 bg-gray-600 rounded-full overflow-hidden">
                        <div className={`h-full rounded-full ${valueScore >= 75 ? 'bg-green-500' : valueScore >= 50 ? 'bg-amber-500' : valueScore >= 25 ? 'bg-orange-500' : 'bg-red-500'}`} style={{ width: `${valueScore}%` }} />
                      </div>
                      <span className={`text-xs font-mono ${valueColor}`}>{valueScore}</span>
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    {comp ? (
                      <span className={`px-2 py-0.5 rounded text-xs font-medium ${compColor.bg} ${compColor.text}`}>
                        {comp.label}
                      </span>
                    ) : (
                      <span className="text-gray-500 text-xs">-</span>
                    )}
                  </td>
                  <td className="px-4 py-3">
                    <span className={`text-xs ${recencyColor}`}>{recencyLabel}</span>
                  </td>
                  <td className="px-4 py-3">
                    <span className={`px-2 py-0.5 rounded text-xs font-medium ${tierBadgeClass(player.tier)}`}>
                      {normalizeTier(player.tier)}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-center">
                    {player.isAppUser ? (
                      <span className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-green-500/20" title="App user">
                        <Check size={14} className="text-green-400" />
                      </span>
                    ) : (
                      <span className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-gray-700" title="Not on app">
                        <X size={14} className="text-gray-500" />
                      </span>
                    )}
                  </td>
                </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </>
  );
}


// ==========================================
// GAME PERFORMANCE REPORT
// ==========================================
function GamePerformanceReport({ data, loading, formatHours }) {
  if (loading) {
    return (
      <div className="bg-gray-800 rounded-xl p-16 flex flex-col items-center justify-center">
        <RefreshCw className="w-8 h-8 text-amber-500 animate-spin mb-4" />
        <p className="text-gray-400">Loading game data...</p>
      </div>
    );
  }

  if (!data?.games?.length) {
    return (
      <div className="bg-gray-800 rounded-xl p-16 flex flex-col items-center justify-center">
        <Gamepad2 className="w-12 h-12 text-gray-600 mb-3" />
        <p className="text-gray-400">No game activity found for this date range</p>
      </div>
    );
  }

  // Calculate max values for bar widths
  const maxHours = Math.max(...data.games.map(g => g.totalHours || 0));
  const maxPlayers = Math.max(...data.games.map(g => g.uniquePlayers || 0));

  return (
    <>
      {/* Summary Stats */}
      {data?.summary && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="bg-gray-800 rounded-xl p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-amber-500/20 rounded-lg flex items-center justify-center">
                <Gamepad2 className="w-5 h-5 text-amber-500" />
              </div>
              <div>
                <div className="text-2xl font-bold text-white">{data.games.length}</div>
                <div className="text-sm text-gray-400">Active Games</div>
              </div>
            </div>
          </div>
          <div className="bg-gray-800 rounded-xl p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-green-500/20 rounded-lg flex items-center justify-center">
                <Clock className="w-5 h-5 text-green-500" />
              </div>
              <div>
                <div className="text-2xl font-bold text-white">{formatHours(data.summary?.totalHours)}</div>
                <div className="text-sm text-gray-400">Total Table Hours</div>
              </div>
            </div>
          </div>
          <div className="bg-gray-800 rounded-xl p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-blue-500/20 rounded-lg flex items-center justify-center">
                <Users className="w-5 h-5 text-blue-500" />
              </div>
              <div>
                <div className="text-2xl font-bold text-white">{data.summary?.totalPlayers?.toLocaleString()}</div>
                <div className="text-sm text-gray-400">Total Unique Players</div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Games Performance */}
      <div className="bg-gray-800 rounded-xl overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-700">
          <h2 className="text-lg font-semibold text-white flex items-center gap-2">
            <Gamepad2 className="text-amber-500" size={20} />
            Game Performance
          </h2>
          <p className="text-sm text-gray-400 mt-1">
            Hours and player counts by game type
          </p>
        </div>

        <div className="p-6 space-y-6">
          {data.games.map((game, index) => (
            <div key={game.gameName || index} className="space-y-2">
              <div className="flex items-center justify-between">
                <span className="font-medium text-white">{game.gameName}</span>
                <div className="flex items-center gap-4 text-sm">
                  <span className="text-gray-400">
                    <Clock size={14} className="inline mr-1" />
                    {formatHours(game.totalHours)}
                  </span>
                  <span className="text-gray-400">
                    <Users size={14} className="inline mr-1" />
                    {game.uniquePlayers} players
                  </span>
                  <span className="text-amber-500 font-medium">
                    {game.sessionCount} sessions
                  </span>
                </div>
              </div>
              
              {/* Hours Bar */}
              <div className="h-4 bg-gray-700 rounded-full overflow-hidden">
                <div 
                  className="h-full bg-gradient-to-r from-amber-500 to-amber-400 rounded-full transition-all duration-500"
                  style={{ width: `${maxHours ? (game.totalHours / maxHours) * 100 : 0}%` }}
                />
              </div>
              
              {/* Players Bar */}
              <div className="h-2 bg-gray-700 rounded-full overflow-hidden">
                <div 
                  className="h-full bg-gradient-to-r from-blue-500 to-blue-400 rounded-full transition-all duration-500"
                  style={{ width: `${maxPlayers ? (game.uniquePlayers / maxPlayers) * 100 : 0}%` }}
                />
              </div>
            </div>
          ))}
        </div>
      </div>
    </>
  );
}


// ==========================================
// PLAYER RETENTION REPORT
// ==========================================
function PlayerRetentionReport({ data, loading, formatDateDisplay }) {
  if (loading) {
    return (
      <div className="bg-gray-800 rounded-xl p-16 flex flex-col items-center justify-center">
        <RefreshCw className="w-8 h-8 text-amber-500 animate-spin mb-4" />
        <p className="text-gray-400">Loading retention data...</p>
      </div>
    );
  }

  if (!data) {
    return (
      <div className="bg-gray-800 rounded-xl p-16 flex flex-col items-center justify-center">
        <UserCheck className="w-12 h-12 text-gray-600 mb-3" />
        <p className="text-gray-400">No retention data found for this date range</p>
      </div>
    );
  }

  // Map API response fields
  const summary = data.summary || {};
  const atRiskPlayers = data.atRiskPlayers || [];

  return (
    <>
      {/* Retention Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="bg-gray-800 rounded-xl p-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-amber-500/20 rounded-lg flex items-center justify-center">
              <Users className="w-5 h-5 text-amber-500" />
            </div>
            <div>
              <div className="text-2xl font-bold text-white">{(summary.totalVisitors || 0).toLocaleString()}</div>
              <div className="text-sm text-gray-400">Total Players</div>
            </div>
          </div>
        </div>
        <div className="bg-gray-800 rounded-xl p-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-green-500/20 rounded-lg flex items-center justify-center">
              <TrendingUp className="w-5 h-5 text-green-500" />
            </div>
            <div>
              <div className="text-2xl font-bold text-white">{(summary.firstTimeVisitors || 0).toLocaleString()}</div>
              <div className="text-sm text-gray-400">First-Time Players</div>
            </div>
          </div>
        </div>
        <div className="bg-gray-800 rounded-xl p-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-blue-500/20 rounded-lg flex items-center justify-center">
              <UserCheck className="w-5 h-5 text-blue-500" />
            </div>
            <div>
              <div className="text-2xl font-bold text-white">{(summary.repeatVisitors || 0).toLocaleString()}</div>
              <div className="text-sm text-gray-400">Returning Players</div>
            </div>
          </div>
        </div>
        <div className="bg-gray-800 rounded-xl p-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-red-500/20 rounded-lg flex items-center justify-center">
              <AlertTriangle className="w-5 h-5 text-red-500" />
            </div>
            <div>
              <div className="text-2xl font-bold text-white">{atRiskPlayers.length}</div>
              <div className="text-sm text-gray-400">At-Risk Players</div>
            </div>
          </div>
        </div>
      </div>

      {/* At-Risk Players List */}
      {atRiskPlayers.length > 0 && (
        <div className="bg-gray-800 rounded-xl overflow-hidden">
          <div className="px-6 py-4 border-b border-gray-700">
            <h2 className="text-lg font-semibold text-white flex items-center gap-2">
              <AlertTriangle className="text-red-500" size={20} />
              At-Risk Players (30-90 days inactive)
            </h2>
            <p className="text-sm text-gray-400 mt-1">
              Players who haven't visited in 30-90 days
            </p>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-700">
                <tr>
                  <th className="px-6 py-4 text-left text-sm font-semibold text-gray-300">Player</th>
                  <th className="px-6 py-4 text-left text-sm font-semibold text-gray-300">Member #</th>
                  <th className="px-6 py-4 text-left text-sm font-semibold text-gray-300">Last Visit</th>
                  <th className="px-6 py-4 text-left text-sm font-semibold text-gray-300">Days Inactive</th>
                  <th className="px-6 py-4 text-left text-sm font-semibold text-gray-300">Total Hours</th>
                  <th className="px-6 py-4 text-left text-sm font-semibold text-gray-300">Tier</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-700">
                {atRiskPlayers.slice(0, 20).map((player, index) => (
                  <tr key={player.playerId || index} className="hover:bg-gray-750">
                    <td className="px-6 py-4">
                      <span className="font-medium text-white">
                        {player.playerName || `${player.firstName || ''} ${player.lastName || ''}`.trim() || 'Unknown'}
                      </span>
                    </td>
                    <td className="px-6 py-4">
                      <span className="text-gray-400 font-mono text-sm">{player.memberNumber || '-'}</span>
                    </td>
                    <td className="px-6 py-4">
                      <span className="text-gray-300">{formatDateDisplay(player.lastVisitDate || player.lastVisit)}</span>
                    </td>
                    <td className="px-6 py-4">
                      <span className={`font-medium ${
                        player.daysSinceVisit > 60 ? 'text-red-400' :
                        player.daysSinceVisit > 45 ? 'text-yellow-400' :
                        'text-orange-400'
                      }`}>
                        {player.daysSinceVisit} days
                      </span>
                    </td>
                    <td className="px-6 py-4">
                      <span className="text-gray-300">{player.totalHoursAllTime || '-'}</span>
                    </td>
                    <td className="px-6 py-4">
                      <span className={`px-2 py-1 rounded text-xs font-medium ${tierBadgeClass(player.tier)}`}>
                        {resolveTierName(player.tier)}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </>
  );
}


// ==========================================
// LAPSED PLAYERS (WIN-BACK ANALYSIS) REPORT
// ==========================================
function LapsedPlayersReport({ formatHours }) {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [showConfig, setShowConfig] = useState(false);
  
  // Filter state
  const [minDaysLapsed, setMinDaysLapsed] = useState(15);
  const [maxDaysLapsed, setMaxDaysLapsed] = useState('');
  const [gameTypeFilter, setGameTypeFilter] = useState('all');
  const [appUsersOnly, setAppUsersOnly] = useState(false);
  
  // Sort state
  const [sortField, setSortField] = useState('finalScore');
  const [sortDirection, setSortDirection] = useState('desc');
  
  // Default algorithm config (shared source of truth)
  const DEFAULT_CONFIG = DEFAULT_WINBACK_CONFIG;

  // Load saved config from localStorage or use defaults (backend sync below)
  const loadSavedConfig = () => {
    try {
      const saved = localStorage.getItem('winback-algorithm-config');
      if (saved) {
        return { ...DEFAULT_CONFIG, ...JSON.parse(saved) };
      }
    } catch (e) {
      console.error('Error loading saved config:', e);
    }
    return DEFAULT_CONFIG;
  };
  
  // Scoring configuration (algorithm weights)
  const [config, setConfig] = useState(loadSavedConfig);

  // Load config from backend on mount (falls back to localStorage/defaults)
  useEffect(() => {
    const fetchConfig = async () => {
      try {
        const res = await api.get('/admin/settings/winback-algorithm');
        if (res.data?.success && res.data?.config && !res.data?.isDefault) {
          const merged = { ...DEFAULT_CONFIG, ...res.data.config };
          setConfig(merged);
          localStorage.setItem('winback-algorithm-config', JSON.stringify(merged));
        }
      } catch (e) {
        console.log('Could not load win-back config from backend, using local');
      }
    };
    fetchConfig();
  }, []);
  
  // Save current config as new default (backend + localStorage)
  const saveAsDefault = async () => {
    try {
      localStorage.setItem('winback-algorithm-config', JSON.stringify(config));
      await api.put('/admin/settings/winback-algorithm', config);
      toast.success('Algorithm settings saved (synced to all devices)');
    } catch (e) {
      console.error('Error saving config:', e);
      toast.warning('Saved locally but failed to sync to server');
    }
  };
  
  // Reset to original defaults (not saved defaults)
  const resetToDefaults = () => {
    setConfig(DEFAULT_CONFIG);
  };

  // Fetch lapsed players
  const fetchData = async () => {
    try {
      setLoading(true);
      const params = { 
        minDaysLapsed,
        limit: 1000,
      };
      if (maxDaysLapsed) params.maxDaysLapsed = maxDaysLapsed;
      if (gameTypeFilter !== 'all') params.gameType = gameTypeFilter;
      
      const response = await api.get('/admin/reports/lapsed-players', { params });
      setData(response.data);
    } catch (error) {
      console.error('Error fetching lapsed players:', error);
      toast.error('Failed to load lapsed players report');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [minDaysLapsed, maxDaysLapsed, gameTypeFilter]);

  // Process players with scoring (shared win-back algorithm)
  const processedPlayers = useMemo(() => {
    if (!data?.players) return [];

    const players = data.players.map(player => {
      const valueScore = computeValueScore(player, config);
      const recencyMultiplier = getRecencyMultiplier(player.DaysSinceLastPlay, config);
      const finalScore = computeFinalScore(valueScore, recencyMultiplier);
      const comp = computeComp(finalScore, player.AvgWager, config);
      const compColor = getCompColor(comp.amount);

      return {
        ...player,
        valueScore,
        recencyMultiplier,
        finalScore,
        compAmount: comp.amount,
        compRecommendation: comp.amount > 0 ? `$${comp.amount}` : '',
        compBaseAmount: comp.baseAmount,
        compColor: compColor.text,
        compBgColor: compColor.bg,
      };
    });

    // Sort based on current sort field and direction
    const visible = appUsersOnly ? players.filter((pl) => pl.isAppUser) : players;
    return visible.sort((a, b) => {
      let aVal = sortField === 'compRecommendation' ? a.compAmount : a[sortField];
      let bVal = sortField === 'compRecommendation' ? b.compAmount : b[sortField];

      // Handle string comparisons
      if (typeof aVal === 'string' && typeof bVal === 'string') {
        return sortDirection === 'asc'
          ? aVal.localeCompare(bVal)
          : bVal.localeCompare(aVal);
      }

      // Handle numeric comparisons
      aVal = aVal || 0;
      bVal = bVal || 0;
      return sortDirection === 'asc' ? aVal - bVal : bVal - aVal;
    });
  }, [data?.players, config, sortField, sortDirection, appUsersOnly]);
  
  // Handle sort
  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('desc');
    }
  };

  // Summary stats for comp recommendations (dynamic hybrid amounts)
  const compSummary = useMemo(() => {
    const summary = { comped: 0, none: 0, totalValue: 0, avg: 0 };
    processedPlayers.forEach(p => {
      if (p.compAmount > 0) {
        summary.comped++;
        summary.totalValue += p.compAmount;
      } else {
        summary.none++;
      }
    });
    summary.avg = summary.comped > 0 ? Math.round(summary.totalValue / summary.comped) : 0;
    return summary;
  }, [processedPlayers]);

  // ---- Multi-select + Send Win-Back ----
  const [selectedIds, setSelectedIds] = useState(() => new Set());
  const [sendModalOpen, setSendModalOpen] = useState(false);
  const [sending, setSending] = useState(false);
  const [winbackAck, setWinbackAck] = useState(null); // pending (no-account) recipients — require acknowledgment
  const [sendExpiryDays, setSendExpiryDays] = useState(30);
  const [sendMessage, setSendMessage] = useState(
    "We miss you at EDH Casino! Here's a comp to welcome you back."
  );

  const compedPlayers = useMemo(
    () => processedPlayers.filter(p => p.compAmount > 0),
    [processedPlayers]
  );

  const toggleSelect = (memberId) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(memberId)) next.delete(memberId);
      else next.add(memberId);
      return next;
    });
  };

  const allCompedSelected =
    compedPlayers.length > 0 && compedPlayers.every(p => selectedIds.has(p.MemberID));

  const toggleSelectAll = () => {
    setSelectedIds(prev => {
      if (compedPlayers.length > 0 && compedPlayers.every(p => prev.has(p.MemberID))) {
        return new Set();
      }
      return new Set(compedPlayers.map(p => p.MemberID));
    });
  };

  const selectedPlayers = useMemo(
    () => processedPlayers.filter(p => selectedIds.has(p.MemberID) && p.compAmount > 0),
    [processedPlayers, selectedIds]
  );
  const selectedTotal = selectedPlayers.reduce((sum, p) => sum + p.compAmount, 0);

  // Send win-back comps to the selected players via the Quick Send engine.
  // Players are grouped by comp amount so each amount becomes one reward.
  const sendWinBack = async () => {
    if (!selectedPlayers.length) return;
    setSending(true);
    try {
      const byAmount = {};
      selectedPlayers.forEach(p => {
        (byAmount[p.compAmount] = byAmount[p.compAmount] || []).push(p.MemberID);
      });

      let totalAssigned = 0;
      const allUnmatched = [];
      for (const [amount, memberNumbers] of Object.entries(byAmount)) {
        const amt = parseInt(amount, 10);
        const resp = await api.post('/admin/rewards/quick-send', {
          title: `We Miss You - $${amt} Comp`,
          description: `A $${amt} win-back comp from EDH Casino.`,
          rewardValue: amt,
          rewardValueType: 'dollar',
          icon: 'gift',
          category: 'winback',
          requiresStaffAuth: true,
          expiresAfterDays: parseInt(sendExpiryDays, 10) || 30,
          sendNotification: true,
          notificationMessage: sendMessage,
          recipients: { memberNumbers },
        });
        const s = (resp && resp.data && resp.data.summary) || {};
        totalAssigned += Number(s.assigned || 0);
        if (Array.isArray(s.pendingMemberNumbers)) allUnmatched.push(...s.pendingMemberNumbers);
      }

      // Suppress the "sent to 0" toast when every recipient was pending — the
      // acknowledgment step below explains the outcome instead.
      if (!(allUnmatched.length > 0 && totalAssigned === 0)) {
        toast.success(`Win-back sent to ${totalAssigned} player(s)`);
      }
      setSelectedIds(new Set());
      setSendModalOpen(false);
      if (allUnmatched.length > 0) {
        // Pending (no-account) recipients: require an explicit acknowledgment so
        // staff understand the comp was NOT delivered and the member must download
        // the app to receive it (it applies when they link their account).
        console.warn('[WinBack] Unmatched member numbers:', allUnmatched);
        setWinbackAck({ unmatched: allUnmatched, assigned: totalAssigned });
      }
    } catch (error) {
      console.error('Error sending win-back:', error);
      toast.error(error.response?.data?.error || 'Failed to send win-back');
    } finally {
      setSending(false);
    }
  };

  // Send to a single player row
  const sendSingle = (player) => {
    setSelectedIds(new Set([player.MemberID]));
    setSendModalOpen(true);
  };

  // Export to CSV
  const exportToCSV = () => {
    if (!processedPlayers.length) return;

    const headers = [
      'Player Name', 'Member ID', 'Email', 'Phone', 'Sign Up Date', 'Last Day Played', 
      'Days Since Last Play', 'Sessions', 'Visits', 'Total Hours', 'Avg Wager', 'Avg Days Between Visits',
      'Most Played Game', 'Game Type', 'Tier', 'Value Score', 'Recency Multiplier', 'Final Score',
      'Recommended Comp'
    ];
    
    const rows = processedPlayers.map(p => [
      p.PlayerName,
      p.MemberID,
      p.Email || '',
      p.Phone || '',
      p.SignUpDate ? new Date(p.SignUpDate).toLocaleDateString() : '',
      p.LastDayPlayed ? new Date(p.LastDayPlayed).toLocaleDateString() : '',
      p.DaysSinceLastPlay,
      p.TotalSessions,
      p.TotalVisits,
      p.TotalHours,
      p.AvgWager != null ? p.AvgWager : '',
      p.AvgDaysBetweenVisits != null ? p.AvgDaysBetweenVisits : '',
      p.MostPlayedGame || '',
      p.GameType,
      p.isAppUser ? resolveTierName(p.Tier) : '',
      p.valueScore,
      p.recencyMultiplier,
      p.finalScore,
      p.compAmount > 0 ? p.compAmount : 'None',
    ]);

    const csvContent = [
      headers.join(','),
      ...rows.map(row => row.map(cell => `"${cell}"`).join(','))
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `lapsed-players-winback-${new Date().toISOString().split('T')[0]}.csv`;
    link.click();
  };

  if (loading) {
    return (
      <div className="bg-gray-800 rounded-xl p-16 flex flex-col items-center justify-center">
        <RefreshCw className="w-8 h-8 text-amber-500 animate-spin mb-4" />
        <p className="text-gray-400">Loading lapsed players data...</p>
      </div>
    );
  }

  return (
    <>
      {/* Summary Stats */}
      <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
        <div className="bg-gray-800 rounded-xl p-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-amber-500/20 rounded-lg flex items-center justify-center">
              <Users className="w-5 h-5 text-amber-500" />
            </div>
            <div>
              <div className="text-2xl font-bold text-white">{processedPlayers.length}</div>
              <div className="text-sm text-gray-400">Lapsed Players</div>
            </div>
          </div>
        </div>
        <div className="bg-gray-800 rounded-xl p-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-green-500/20 rounded-lg flex items-center justify-center">
              <DollarSign className="w-5 h-5 text-green-500" />
            </div>
            <div>
              <div className="text-2xl font-bold text-green-400">{compSummary.comped}</div>
              <div className="text-sm text-gray-400">Players to Comp</div>
            </div>
          </div>
        </div>
        <div className="bg-gray-800 rounded-xl p-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-yellow-500/20 rounded-lg flex items-center justify-center">
              <DollarSign className="w-5 h-5 text-yellow-500" />
            </div>
            <div>
              <div className="text-2xl font-bold text-yellow-400">${compSummary.avg}</div>
              <div className="text-sm text-gray-400">Avg Comp</div>
            </div>
          </div>
        </div>
        <div className="bg-gray-800 rounded-xl p-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-purple-500/20 rounded-lg flex items-center justify-center">
              <TrendingUp className="w-5 h-5 text-purple-500" />
            </div>
            <div>
              <div className="text-2xl font-bold text-purple-400">${compSummary.totalValue.toLocaleString()}</div>
              <div className="text-sm text-gray-400">Total Budget</div>
            </div>
          </div>
        </div>
        <div className="bg-gray-800 rounded-xl p-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-blue-500/20 rounded-lg flex items-center justify-center">
              <Check className="w-5 h-5 text-blue-500" />
            </div>
            <div>
              <div className="text-2xl font-bold text-blue-400">{selectedPlayers.length}</div>
              <div className="text-sm text-gray-400">Selected (${selectedTotal.toLocaleString()})</div>
            </div>
          </div>
        </div>
      </div>

      {/* Filters and Configuration */}
      <div className="bg-gray-800 rounded-xl p-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold text-white flex items-center gap-2">
            <Sliders className="text-amber-500" size={20} />
            Filters & Algorithm Configuration
          </h2>
          <button
            onClick={() => setShowConfig(!showConfig)}
            className="flex items-center gap-2 px-3 py-1.5 bg-gray-700 text-gray-300 rounded-lg hover:bg-gray-600 transition-colors text-sm"
          >
            <Sliders size={14} />
            {showConfig ? 'Hide' : 'Show'} Algorithm Settings
          </button>
        </div>

        {/* Basic Filters */}
        <div className="flex flex-wrap gap-4 mb-4">
          <div>
            <label className="block text-sm text-gray-400 mb-1">Min Days Lapsed</label>
            <input
              type="number"
              value={minDaysLapsed}
              onChange={(e) => setMinDaysLapsed(parseInt(e.target.value) || 15)}
              min="1"
              className="w-24 px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white text-sm focus:outline-none focus:border-amber-500"
            />
          </div>
          <div>
            <label className="block text-sm text-gray-400 mb-1">Max Days Lapsed</label>
            <input
              type="number"
              value={maxDaysLapsed}
              onChange={(e) => setMaxDaysLapsed(e.target.value)}
              placeholder="No limit"
              className="w-24 px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white text-sm focus:outline-none focus:border-amber-500"
            />
          </div>
          <div>
            <label className="block text-sm text-gray-400 mb-1">Game Type</label>
            <select
              value={gameTypeFilter}
              onChange={(e) => setGameTypeFilter(e.target.value)}
              className="px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white text-sm focus:outline-none focus:border-amber-500"
            >
              <option value="all">All Games</option>
              <option value="poker">Poker Only</option>
              <option value="calgame">Cal Games Only</option>
            </select>
          </div>
          <div className="flex items-end">
            <label className="flex items-center gap-2 px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white text-sm cursor-pointer select-none">
              <input
                type="checkbox"
                checked={appUsersOnly}
                onChange={(e) => setAppUsersOnly(e.target.checked)}
                className="accent-amber-500 cursor-pointer"
              />
              App users only
            </label>
          </div>
          <div className="flex items-end">
            <button
              onClick={fetchData}
              className="flex items-center gap-2 px-4 py-2 bg-amber-500 text-black font-medium rounded-lg hover:bg-amber-400 transition-colors"
            >
              <RefreshCw size={16} />
              Refresh
            </button>
          </div>
        </div>

        {/* Algorithm Configuration Panel */}
        {showConfig && (
          <div className="mt-6 pt-6 border-t border-gray-700">
            <WinbackAlgorithmConfig
              config={config}
              onChange={setConfig}
              onSaveDefault={saveAsDefault}
              onReset={resetToDefaults}
            />
          </div>
        )}
        {false && (
          <div className="mt-6 pt-6 border-t border-gray-700 space-y-6">
            {/* Value Score Weights */}
            <div>
              <h3 className="text-sm font-semibold text-white mb-3 flex items-center gap-2">
                <BarChart3 size={16} className="text-amber-500" />
                Value Score Weights (Total: {config.hoursWeight + config.visitsWeight + config.sessionsWeight})
              </h3>
              <div className="grid grid-cols-3 gap-4">
                <div>
                  <label className="block text-xs text-gray-400 mb-1">Hours Weight ({config.hoursWeight}%)</label>
                  <input
                    type="range"
                    min="0"
                    max="100"
                    value={config.hoursWeight}
                    onChange={(e) => setConfig({ ...config, hoursWeight: parseInt(e.target.value) })}
                    className="w-full accent-amber-500"
                  />
                </div>
                <div>
                  <label className="block text-xs text-gray-400 mb-1">Visits Weight ({config.visitsWeight}%)</label>
                  <input
                    type="range"
                    min="0"
                    max="100"
                    value={config.visitsWeight}
                    onChange={(e) => setConfig({ ...config, visitsWeight: parseInt(e.target.value) })}
                    className="w-full accent-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-xs text-gray-400 mb-1">Sessions Weight ({config.sessionsWeight}%)</label>
                  <input
                    type="range"
                    min="0"
                    max="100"
                    value={config.sessionsWeight}
                    onChange={(e) => setConfig({ ...config, sessionsWeight: parseInt(e.target.value) })}
                    className="w-full accent-green-500"
                  />
                </div>
              </div>
            </div>

            {/* Recency Multipliers */}
            <div>
              <h3 className="text-sm font-semibold text-white mb-3 flex items-center gap-2">
                <Clock size={16} className="text-amber-500" />
                Recency Multipliers
              </h3>
              <div className="grid grid-cols-4 gap-4">
                <div>
                  <label className="block text-xs text-gray-400 mb-1">15-45 days ({config.recencyBoost15to45}x)</label>
                  <input
                    type="range"
                    min="0.1"
                    max="2"
                    step="0.1"
                    value={config.recencyBoost15to45}
                    onChange={(e) => setConfig({ ...config, recencyBoost15to45: parseFloat(e.target.value) })}
                    className="w-full accent-green-500"
                  />
                </div>
                <div>
                  <label className="block text-xs text-gray-400 mb-1">46-90 days ({config.recencyBoost46to90}x)</label>
                  <input
                    type="range"
                    min="0.1"
                    max="2"
                    step="0.1"
                    value={config.recencyBoost46to90}
                    onChange={(e) => setConfig({ ...config, recencyBoost46to90: parseFloat(e.target.value) })}
                    className="w-full accent-yellow-500"
                  />
                </div>
                <div>
                  <label className="block text-xs text-gray-400 mb-1">91-180 days ({config.recencyBoost91to180}x)</label>
                  <input
                    type="range"
                    min="0.1"
                    max="2"
                    step="0.1"
                    value={config.recencyBoost91to180}
                    onChange={(e) => setConfig({ ...config, recencyBoost91to180: parseFloat(e.target.value) })}
                    className="w-full accent-orange-500"
                  />
                </div>
                <div>
                  <label className="block text-xs text-gray-400 mb-1">180+ days ({config.recencyBoost180plus}x)</label>
                  <input
                    type="range"
                    min="0.1"
                    max="2"
                    step="0.1"
                    value={config.recencyBoost180plus}
                    onChange={(e) => setConfig({ ...config, recencyBoost180plus: parseFloat(e.target.value) })}
                    className="w-full accent-red-500"
                  />
                </div>
              </div>
            </div>

            {/* Comp Thresholds */}
            <div>
              <h3 className="text-sm font-semibold text-white mb-3 flex items-center gap-2">
                <DollarSign size={16} className="text-amber-500" />
                Comp Thresholds (Final Score)
              </h3>
              <div className="grid grid-cols-3 gap-4">
                <div>
                  <label className="block text-xs text-gray-400 mb-1">$100 Comp (â‰¥ {config.threshold100})</label>
                  <input
                    type="range"
                    min="0"
                    max="100"
                    value={config.threshold100}
                    onChange={(e) => setConfig({ ...config, threshold100: parseInt(e.target.value) })}
                    className="w-full accent-green-500"
                  />
                </div>
                <div>
                  <label className="block text-xs text-gray-400 mb-1">$50 Comp (â‰¥ {config.threshold50})</label>
                  <input
                    type="range"
                    min="0"
                    max="100"
                    value={config.threshold50}
                    onChange={(e) => setConfig({ ...config, threshold50: parseInt(e.target.value) })}
                    className="w-full accent-yellow-500"
                  />
                </div>
                <div>
                  <label className="block text-xs text-gray-400 mb-1">$25 Comp (â‰¥ {config.threshold25})</label>
                  <input
                    type="range"
                    min="0"
                    max="100"
                    value={config.threshold25}
                    onChange={(e) => setConfig({ ...config, threshold25: parseInt(e.target.value) })}
                    className="w-full accent-orange-500"
                  />
                </div>
              </div>
            </div>

            {/* Action Buttons */}
            <div className="flex justify-end gap-4">
              <button
                onClick={resetToDefaults}
                className="text-sm text-gray-400 hover:text-white transition-colors"
              >
                Reset to Defaults
              </button>
              <button
                onClick={saveAsDefault}
                className="flex items-center gap-1 text-sm text-amber-500 hover:text-amber-400 transition-colors"
              >
                <Sliders size={14} />
                Save as Default
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Players Table */}
      <div className="bg-gray-800 rounded-xl overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-700 flex items-center justify-between">
          <div>
            <h2 className="text-lg font-semibold text-white flex items-center gap-2">
              <DollarSign className="text-amber-500" size={20} />
              Lapsed Players Win-Back List
            </h2>
            <p className="text-sm text-gray-400 mt-1">
              {processedPlayers.length} players â€¢ Click column headers to sort
            </p>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => setSendModalOpen(true)}
              disabled={!selectedPlayers.length}
              className="flex items-center gap-2 px-4 py-2 bg-amber-500 text-black font-medium rounded-lg hover:bg-amber-400 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
            >
              <Gift size={18} />
              Send Win-Back{selectedPlayers.length ? ` (${selectedPlayers.length})` : ''}
            </button>
            <button
              onClick={exportToCSV}
              disabled={!processedPlayers.length}
              className="flex items-center gap-2 px-4 py-2 bg-gray-700 text-white rounded-lg hover:bg-gray-600 transition-colors disabled:opacity-50"
            >
              <Download size={18} />
              Export CSV
            </button>
          </div>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-700">
              <tr>
                <th className="px-4 py-3 text-center w-10">
                  <input
                    type="checkbox"
                    checked={allCompedSelected}
                    onChange={toggleSelectAll}
                    className="accent-amber-500 cursor-pointer"
                    title="Select all comp-eligible players"
                  />
                </th>
                <th 
                  className="px-4 py-3 text-left text-xs font-semibold text-gray-400 uppercase cursor-pointer hover:text-white"
                  onClick={() => handleSort('PlayerName')}
                >
                  <div className="flex items-center gap-1">
                    Player
                    <ArrowUpDown size={12} className={sortField === 'PlayerName' ? 'text-amber-500' : 'opacity-50'} />
                  </div>
                </th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-400 uppercase">Member ID</th>
                <th 
                  className="px-4 py-3 text-left text-xs font-semibold text-gray-400 uppercase cursor-pointer hover:text-white"
                  onClick={() => handleSort('LastDayPlayed')}
                >
                  <div className="flex items-center gap-1">
                    Last Played
                    <ArrowUpDown size={12} className={sortField === 'LastDayPlayed' ? 'text-amber-500' : 'opacity-50'} />
                  </div>
                </th>
                <th 
                  className="px-4 py-3 text-center text-xs font-semibold text-gray-400 uppercase cursor-pointer hover:text-white"
                  onClick={() => handleSort('DaysSinceLastPlay')}
                >
                  <div className="flex items-center justify-center gap-1">
                    Days Lapsed
                    <ArrowUpDown size={12} className={sortField === 'DaysSinceLastPlay' ? 'text-amber-500' : 'opacity-50'} />
                  </div>
                </th>
                <th 
                  className="px-4 py-3 text-center text-xs font-semibold text-gray-400 uppercase cursor-pointer hover:text-white"
                  onClick={() => handleSort('TotalVisits')}
                >
                  <div className="flex items-center justify-center gap-1">
                    Visits
                    <ArrowUpDown size={12} className={sortField === 'TotalVisits' ? 'text-amber-500' : 'opacity-50'} />
                  </div>
                </th>
                <th 
                  className="px-4 py-3 text-center text-xs font-semibold text-gray-400 uppercase cursor-pointer hover:text-white"
                  onClick={() => handleSort('TotalHours')}
                >
                  <div className="flex items-center justify-center gap-1">
                    Hours
                    <ArrowUpDown size={12} className={sortField === 'TotalHours' ? 'text-amber-500' : 'opacity-50'} />
                  </div>
                </th>
                <th 
                  className="px-4 py-3 text-center text-xs font-semibold text-gray-400 uppercase cursor-pointer hover:text-white"
                  onClick={() => handleSort('AvgWager')}
                >
                  <div className="flex items-center justify-center gap-1">
                    Avg Wager
                    <ArrowUpDown size={12} className={sortField === 'AvgWager' ? 'text-amber-500' : 'opacity-50'} />
                  </div>
                </th>
                <th 
                  className="px-4 py-3 text-center text-xs font-semibold text-gray-400 uppercase cursor-pointer hover:text-white"
                  onClick={() => handleSort('AvgDaysBetweenVisits')}
                >
                  <div className="flex items-center justify-center gap-1">
                    Avg Gap
                    <ArrowUpDown size={12} className={sortField === 'AvgDaysBetweenVisits' ? 'text-amber-500' : 'opacity-50'} />
                  </div>
                </th>
                <th 
                  className="px-4 py-3 text-left text-xs font-semibold text-gray-400 uppercase cursor-pointer hover:text-white"
                  onClick={() => handleSort('GameType')}
                >
                  <div className="flex items-center gap-1">
                    Game Type
                    <ArrowUpDown size={12} className={sortField === 'GameType' ? 'text-amber-500' : 'opacity-50'} />
                  </div>
                </th>
                <th 
                  className="px-4 py-3 text-center text-xs font-semibold text-gray-400 uppercase cursor-pointer hover:text-white"
                  onClick={() => handleSort('valueScore')}
                >
                  <div className="flex items-center justify-center gap-1">
                    Value Score
                    <ArrowUpDown size={12} className={sortField === 'valueScore' ? 'text-amber-500' : 'opacity-50'} />
                  </div>
                </th>
                <th 
                  className="px-4 py-3 text-center text-xs font-semibold text-gray-400 uppercase cursor-pointer hover:text-white"
                  onClick={() => handleSort('recencyMultiplier')}
                >
                  <div className="flex items-center justify-center gap-1">
                    Recency
                    <ArrowUpDown size={12} className={sortField === 'recencyMultiplier' ? 'text-amber-500' : 'opacity-50'} />
                  </div>
                </th>
                <th 
                  className="px-4 py-3 text-center text-xs font-semibold text-gray-400 uppercase cursor-pointer hover:text-white"
                  onClick={() => handleSort('finalScore')}
                >
                  <div className="flex items-center justify-center gap-1">
                    Final Score
                    <ArrowUpDown size={12} className={sortField === 'finalScore' ? 'text-amber-500' : 'opacity-50'} />
                  </div>
                </th>
                <th 
                  className="px-4 py-3 text-center text-xs font-semibold text-gray-400 uppercase cursor-pointer hover:text-white"
                  onClick={() => handleSort('compRecommendation')}
                >
                  <div className="flex items-center justify-center gap-1">
                    Comp
                    <ArrowUpDown size={12} className={sortField === 'compRecommendation' ? 'text-amber-500' : 'opacity-50'} />
                  </div>
                </th>
                <th className="px-4 py-3 text-center text-xs font-semibold text-gray-400 uppercase">Send</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-700">
              {processedPlayers.slice(0, 100).map((player, index) => (
                <tr key={player.MemberID || index} className="hover:bg-gray-750">
                  <td className="px-4 py-3 text-center">
                    {player.compAmount > 0 ? (
                      <input
                        type="checkbox"
                        checked={selectedIds.has(player.MemberID)}
                        onChange={() => toggleSelect(player.MemberID)}
                        className="accent-amber-500 cursor-pointer"
                      />
                    ) : null}
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-2">
                      <span className="font-medium text-white">{player.PlayerName}</span>
                      {player.isAppUser && (
                        <span className={`px-1.5 py-0.5 text-xs rounded ${tierBadgeClass(player.Tier)}`}>
                          {resolveTierName(player.Tier)}
                        </span>
                      )}
                      {player.isAppUser && (
                        <span className="px-1.5 py-0.5 text-xs rounded bg-blue-500/15 text-blue-300 border border-blue-500/40">App</span>
                      )}
                    </div>
                  </td>
                  <td className="px-4 py-3 text-gray-400 font-mono text-sm">{player.MemberID}</td>
                  <td className="px-4 py-3 text-gray-400 text-sm">
                    {player.LastDayPlayed ? new Date(player.LastDayPlayed).toLocaleDateString() : '-'}
                  </td>
                  <td className="px-4 py-3 text-center">
                    <span className={`font-medium ${
                      player.DaysSinceLastPlay <= 45 ? 'text-green-400' :
                      player.DaysSinceLastPlay <= 90 ? 'text-yellow-400' :
                      player.DaysSinceLastPlay <= 180 ? 'text-orange-400' :
                      'text-red-400'
                    }`}>
                      {player.DaysSinceLastPlay}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-center text-gray-300">{player.TotalVisits}</td>
                  <td className="px-4 py-3 text-center text-gray-300">{player.TotalHours}</td>
                  <td className="px-4 py-3 text-center text-gray-300">
                    {player.AvgWager != null && player.AvgWager > 0 ? `$${player.AvgWager}` : '-'}
                  </td>
                  <td className="px-4 py-3 text-center text-gray-300">
                    {player.AvgDaysBetweenVisits != null ? `${player.AvgDaysBetweenVisits}d` : '-'}
                  </td>
                  <td className="px-4 py-3">
                    <span className={`text-xs px-2 py-1 rounded ${
                      player.GameType === 'Poker' ? 'bg-blue-500/20 text-blue-400' : 'bg-purple-500/20 text-purple-400'
                    }`}>
                      {player.GameType}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-center text-white font-medium">{player.valueScore}</td>
                  <td className="px-4 py-3 text-center text-gray-400">{player.recencyMultiplier}x</td>
                  <td className="px-4 py-3 text-center">
                    <span className="text-amber-500 font-bold">{player.finalScore}</span>
                  </td>
                  <td className="px-4 py-3 text-center">
                    {player.compAmount > 0 ? (
                      <span className={`px-3 py-1 rounded-full text-sm font-bold ${player.compBgColor} ${player.compColor}`}>
                        {player.compRecommendation}
                      </span>
                    ) : (
                      <span className="text-gray-600">-</span>
                    )}
                  </td>
                  <td className="px-4 py-3 text-center">
                    {player.compAmount > 0 ? (
                      <button
                        onClick={() => sendSingle(player)}
                        className="p-1.5 rounded-lg bg-gray-700 text-amber-400 hover:bg-gray-600"
                        title={`Send $${player.compAmount} win-back`}
                      >
                        <Gift size={16} />
                      </button>
                    ) : (
                      <span className="text-gray-600">-</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {processedPlayers.length > 100 && (
          <div className="px-6 py-3 bg-gray-700/50 text-center text-sm text-gray-400">
            Showing first 100 of {processedPlayers.length} players. Export CSV for full list.
          </div>
        )}

        {processedPlayers.length === 0 && (
          <div className="flex flex-col items-center justify-center py-16">
            <Users className="w-12 h-12 text-gray-600 mb-3" />
            <p className="text-gray-400">No lapsed players found matching your criteria</p>
          </div>
        )}
      </div>

      {/* Send Win-Back confirmation modal */}
      {/* Pending-recipient acknowledgment — win-back comps to members with no app account */}
      {winbackAck && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-[60] p-4">
          <div className="bg-gray-800 rounded-xl w-full max-w-md border border-amber-600/60">
            <div className="p-5">
              <div className="flex items-start gap-3">
                <AlertTriangle className="text-amber-400 shrink-0 mt-0.5" size={22} />
                <div>
                  <h3 className="text-lg font-bold text-white">Some comps saved, not delivered yet</h3>
                  <p className="text-sm text-gray-300 mt-1">
                    {winbackAck.assigned > 0 && (
                      <>{winbackAck.assigned} player{winbackAck.assigned !== 1 ? 's' : ''} received the win-back now. </>
                    )}
                    {winbackAck.unmatched.length} member number{winbackAck.unmatched.length !== 1 ? 's' : ''}{' '}
                    {winbackAck.unmatched.length !== 1 ? 'have' : 'has'} no app account, so{' '}
                    {winbackAck.unmatched.length !== 1 ? 'they' : 'that player'} did{' '}
                    <span className="font-semibold text-amber-300">NOT</span> receive the comp.
                  </p>
                  <p className="text-sm text-gray-400 mt-2">
                    It is saved and will apply automatically once they download the EDH Casino app and
                    link their Player's Club account. Let the member know they need to sign up to claim it.
                  </p>
                </div>
              </div>
              <div className="mt-3 max-h-24 overflow-y-auto font-mono text-xs text-amber-100 bg-gray-900/60 rounded p-2">
                {winbackAck.unmatched.join(', ')}
              </div>
              <div className="mt-4 flex items-center justify-between gap-2">
                <button
                  onClick={() => { try { navigator.clipboard?.writeText(winbackAck.unmatched.join('\n')); toast.success('Copied numbers'); } catch { /* clipboard unavailable */ } }}
                  className="text-xs px-3 py-1.5 rounded bg-gray-700 hover:bg-gray-600 text-white"
                >
                  Copy numbers
                </button>
                <button
                  onClick={() => setWinbackAck(null)}
                  className="px-4 py-2 rounded-lg bg-amber-500 hover:bg-amber-600 text-black font-semibold text-sm"
                >
                  Got it, they need the app
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {sendModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4">
          <div className="bg-gray-800 rounded-xl w-full max-w-lg border border-gray-700 shadow-2xl">
            <div className="px-6 py-4 border-b border-gray-700 flex items-center justify-between">
              <h3 className="text-lg font-semibold text-white flex items-center gap-2">
                <Gift className="text-amber-500" size={20} />
                Send Win-Back Comps
              </h3>
              <button onClick={() => setSendModalOpen(false)} className="text-gray-400 hover:text-white">
                <X size={20} />
              </button>
            </div>
            <div className="px-6 py-4 space-y-4">
              <div className="grid grid-cols-2 gap-3">
                <div className="bg-gray-700/50 rounded-lg p-3">
                  <div className="text-2xl font-bold text-white">{selectedPlayers.length}</div>
                  <div className="text-xs text-gray-400">Recipients</div>
                </div>
                <div className="bg-gray-700/50 rounded-lg p-3">
                  <div className="text-2xl font-bold text-amber-400">${selectedTotal.toLocaleString()}</div>
                  <div className="text-xs text-gray-400">Total Comp Value</div>
                </div>
              </div>
              <div>
                <label className="block text-sm text-gray-400 mb-1">Notification message</label>
                <textarea
                  value={sendMessage}
                  onChange={(e) => setSendMessage(e.target.value)}
                  rows={3}
                  className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white text-sm focus:outline-none focus:border-amber-500"
                />
              </div>
              <div className="flex items-center gap-3">
                <label className="text-sm text-gray-400">Expires after</label>
                <input
                  type="number"
                  min="1"
                  value={sendExpiryDays}
                  onChange={(e) => setSendExpiryDays(e.target.value)}
                  className="w-20 px-2 py-1.5 bg-gray-700 border border-gray-600 rounded text-white text-sm focus:outline-none focus:border-amber-500"
                />
                <span className="text-sm text-gray-400">days - staff-authorized redemption</span>
              </div>
              <p className="text-xs text-gray-500">
                Each distinct comp amount becomes one reward assigned to its recipients. Players are notified in the app.
              </p>
            </div>
            <div className="px-6 py-4 border-t border-gray-700 flex justify-end gap-3">
              <button
                onClick={() => setSendModalOpen(false)}
                disabled={sending}
                className="px-4 py-2 text-gray-300 hover:text-white transition-colors disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                onClick={sendWinBack}
                disabled={sending || !selectedPlayers.length}
                className="flex items-center gap-2 px-4 py-2 bg-amber-500 text-black font-medium rounded-lg hover:bg-amber-400 transition-colors disabled:opacity-50"
              >
                {sending ? <RefreshCw size={16} className="animate-spin" /> : <Gift size={16} />}
                {sending ? 'Sending...' : `Send to ${selectedPlayers.length}`}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}


// ==========================================
// PLAYER XP REPORT
// ==========================================

// Tier colors matching the status system
const TIER_COLORS = {
  vip: { bg: 'bg-amber-600/20', text: 'text-amber-500', border: 'border-amber-500', hex: '#CD7F32' },
  elite: { bg: 'bg-zinc-500/20', text: 'text-zinc-400', border: 'border-zinc-400', hex: '#71717A' },
  ambassador: { bg: 'bg-yellow-400/20', text: 'text-yellow-400', border: 'border-yellow-400', hex: '#FFD700' },
  // 'chairman' key in config = Boardmember display (silver)
  chairman: { bg: 'bg-slate-300/20', text: 'text-slate-200', border: 'border-slate-200', hex: '#E5E4E2' },
  boardmember: { bg: 'bg-slate-300/20', text: 'text-slate-200', border: 'border-slate-200', hex: '#E5E4E2' },
  // 'legend' key in config = Chairman display (cyan/platinum)
  legend: { bg: 'bg-cyan-400/20', text: 'text-cyan-300', border: 'border-cyan-300', hex: '#B9F2FF' },
};

function getTierStyle(tier) {
  return TIER_COLORS[tier?.toLowerCase()] || TIER_COLORS.vip;
}

function PlayerXPReport({ data, loading, onRefresh }) {
  const [selectedTier, setSelectedTier] = useState(null);
  const [selectedPlayer, setSelectedPlayer] = useState(null);
  const [playerAchievements, setPlayerAchievements] = useState(null);
  const [achievementsLoading, setAchievementsLoading] = useState(false);
  const [recalculating, setRecalculating] = useState(false);
  const [fullResetting, setFullResetting] = useState(false);
  // When a tier card is selected we fetch that tier's FULL member list from the
  // backend. The default payload only carries the top ~50 by XP, so filtering it
  // client-side under-counted tiers whose members fall outside the top 50 (the
  // list didn't match the tier card counts).
  const [tierPlayers, setTierPlayers] = useState(null);
  const [tierLoading, setTierLoading] = useState(false);

  const handleRecalculateAll = async () => {
    if (!window.confirm('This will recalculate XP for all players using the V2 system. This may take a few moments. Continue?')) {
      return;
    }
    
    setRecalculating(true);
    try {
      const response = await api.post('/admin/reports/recalculate-all-status');
      toast.success(`Recalculation complete: ${response.data.updated} players updated`);
      // Refresh the report data
      if (onRefresh) {
        onRefresh();
      }
    } catch (error) {
      console.error('Error recalculating status:', error);
      toast.error(error.response?.data?.error || 'Failed to recalculate status');
    } finally {
      setRecalculating(false);
    }
  };

  const handleFullResetAll = async () => {
    if (!window.confirm('âš ï¸ FULL RESET: This will clear ALL status history, achievements, and recalculate everything from scratch using start date filtering. This is irreversible. Continue?')) {
      return;
    }
    if (!window.confirm('Are you absolutely sure? This affects ALL players.')) {
      return;
    }

    setFullResetting(true);
    try {
      const response = await api.post('/admin/reports/full-reset-all');
      const d = response.data;
      toast.success(`Full reset complete: ${d.usersRecalculated} users, ${d.achievementsRestored} achievements restored, ${d.statusHistoryDeleted} history cleared`);
      if (onRefresh) {
        onRefresh();
      }
    } catch (error) {
      console.error('Error in full reset:', error);
      toast.error(error.response?.data?.error || 'Failed to full reset');
    } finally {
      setFullResetting(false);
    }
  };

  const fetchPlayerAchievements = async (userId) => {
    try {
      setAchievementsLoading(true);
      const response = await api.get(`/admin/reports/player-achievements/${userId}`);
      console.log('Player achievements response:', response.data);
      console.log('Status object:', response.data?.status);
      setPlayerAchievements(response.data);
    } catch (error) {
      console.error('Error fetching player achievements:', error);
      toast.error('Failed to load player achievements');
    } finally {
      setAchievementsLoading(false);
    }
  };

  const handlePlayerClick = (player) => {
    setSelectedPlayer(player);
    fetchPlayerAchievements(player.userId);
  };

  const closeModal = () => {
    setSelectedPlayer(null);
    setPlayerAchievements(null);
  };

  // Fetch the full member list for the selected tier (the default payload is
  // capped at the top ~50 by XP, which under-counts larger tiers).
  useEffect(() => {
    let cancelled = false;
    if (!selectedTier) {
      setTierPlayers(null);
      return;
    }
    setTierLoading(true);
    api.get('/admin/reports/player-xp', { params: { tier: selectedTier, limit: 2000, sortBy: 'xp', sortOrder: 'desc' } })
      .then(res => { if (!cancelled) setTierPlayers(res.data?.topPlayers || []); })
      .catch(() => { if (!cancelled) setTierPlayers([]); })
      .finally(() => { if (!cancelled) setTierLoading(false); });
    return () => { cancelled = true; };
  }, [selectedTier]);

  // Players shown in the list: the selected tier's full list, else the top players.
  const filteredPlayers = useMemo(() => {
    if (selectedTier) return tierPlayers || [];
    return data?.topPlayers || [];
  }, [data?.topPlayers, selectedTier, tierPlayers]);

  if (loading) {
    return (
      <div className="bg-gray-800 rounded-xl p-16 flex flex-col items-center justify-center">
        <RefreshCw className="w-8 h-8 text-amber-500 animate-spin mb-4" />
        <p className="text-gray-400">Loading player XP data...</p>
      </div>
    );
  }

  if (!data || !data.success) {
    return (
      <div className="bg-gray-800 rounded-xl p-8">
        <div className="flex items-center justify-center text-gray-400 gap-2">
          <AlertTriangle size={20} />
          <span>Failed to load player XP report</span>
        </div>
      </div>
    );
  }

  const { summary, tierDistribution, topPlayers, xpThresholds, config } = data;

  return (
    <>
      <div className="space-y-6">
        {/* Header with Recalculate Buttons */}
        <div className="flex justify-end gap-2">
          <button
            onClick={handleFullResetAll}
            disabled={fullResetting || recalculating || loading}
            className="flex items-center gap-2 px-4 py-2 bg-red-500/20 text-red-400 hover:bg-red-500/30 rounded-lg transition-colors disabled:opacity-50"
          >
            <RefreshCw size={16} className={fullResetting ? 'animate-spin' : ''} />
            {fullResetting ? 'Full Resetting...' : 'Full Reset All'}
          </button>
          <button
            onClick={handleRecalculateAll}
            disabled={recalculating || fullResetting || loading}
            className="flex items-center gap-2 px-4 py-2 bg-amber-500/20 text-amber-500 hover:bg-amber-500/30 rounded-lg transition-colors disabled:opacity-50"
          >
            <RefreshCw size={16} className={recalculating ? 'animate-spin' : ''} />
            {recalculating ? 'Recalculating...' : 'Recalculate All XP'}
          </button>
        </div>

        {/* Summary Cards */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
          <div className="bg-gray-800 rounded-xl p-4">
            <div className="flex items-center gap-2 text-gray-400 text-sm mb-1">
              <Users size={16} />
              <span>Total Players</span>
            </div>
            <div className="text-2xl font-bold text-white">{summary?.totalPlayers?.toLocaleString() || 0}</div>
          </div>
          
          <div className="bg-gray-800 rounded-xl p-4">
            <div className="flex items-center gap-2 text-gray-400 text-sm mb-1">
              <Zap size={16} />
              <span>Total Period XP</span>
            </div>
            <div className="text-2xl font-bold text-amber-500">{summary?.totalXp?.toLocaleString() || 0}</div>
          </div>
          
          <div className="bg-gray-800 rounded-xl p-4">
            <div className="flex items-center gap-2 text-gray-400 text-sm mb-1">
              <TrendingUp size={16} />
              <span>Average XP</span>
            </div>
            <div className="text-2xl font-bold text-white">{summary?.averageXp?.toLocaleString() || 0}</div>
          </div>
          
          <div className="bg-gray-800 rounded-xl p-4">
            <div className="flex items-center gap-2 text-gray-400 text-sm mb-1">
              <Star size={16} />
              <span>Lifetime XP</span>
            </div>
            <div className="text-2xl font-bold text-white">{summary?.totalLifetimeXp?.toLocaleString() || 0}</div>
          </div>
          
          <div className="bg-gray-800 rounded-xl p-4">
            <div className="flex items-center gap-2 text-gray-400 text-sm mb-1">
              <Crown size={16} />
              <span>Lifetime Status</span>
            </div>
            <div className="text-2xl font-bold text-purple-400">{summary?.playersWithLifetimeStatus || 0}</div>
            <div className="text-xs text-gray-500">players</div>
          </div>
        </div>

        {/* Tier Distribution */}
        <div className="bg-gray-800 rounded-xl p-6">
          <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
            <Crown size={20} className="text-amber-500" />
            Tier Distribution
            {selectedTier && (
              <button 
                onClick={() => setSelectedTier(null)}
                className="ml-2 text-xs bg-gray-600 hover:bg-gray-500 px-2 py-1 rounded-full flex items-center gap-1"
              >
                <X size={12} /> Clear filter
              </button>
            )}
          </h3>
          
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
            {tierDistribution?.map(tier => {
              const style = getTierStyle(tier.tier);
              const isSelected = selectedTier?.toLowerCase() === tier.tier.toLowerCase();
              
              return (
                <button
                  key={tier.tier}
                  onClick={() => setSelectedTier(isSelected ? null : tier.tier)}
                  className={`p-4 rounded-xl border-2 transition-all text-left ${style.bg} ${style.border} ${
                    isSelected ? 'ring-2 ring-white ring-offset-2 ring-offset-gray-900' : 'hover:scale-105'
                  }`}
                >
                  <div className={`text-lg font-bold ${style.text}`}>{tier.displayName}</div>
                  <div className="text-2xl font-bold text-white">{tier.count}</div>
                  <div className="text-xs text-gray-400">players</div>
                  <div className="mt-2 text-sm text-gray-300">
                    Avg XP: {tier.averageXp?.toLocaleString()}
                  </div>
                  
                  {/* Level breakdown */}
                  {tier.levels && tier.levels.length > 0 && (
                    <div className="mt-2 pt-2 border-t border-gray-600/50">
                      {tier.levels.filter(l => l.count > 0).map(level => (
                        <div key={level.level} className="flex justify-between text-xs text-gray-400">
                          <span>{level.displayName}</span>
                          <span>{level.count}</span>
                        </div>
                      ))}
                    </div>
                  )}
                </button>
              );
            })}
          </div>
        </div>

        {/* Top Players Table */}
        <div className="bg-gray-800 rounded-xl overflow-hidden">
          <div className="p-4 border-b border-gray-700">
            <h3 className="text-lg font-semibold text-white flex items-center gap-2">
              <Trophy size={20} className="text-amber-500" />
              {selectedTier ? `${selectedTier.charAt(0).toUpperCase() + selectedTier.slice(1)} Players` : 'Top Players by XP'}
              <span className="text-sm font-normal text-gray-400">({filteredPlayers.length})</span>
            </h3>
          </div>
          
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-700/50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-400 uppercase">Rank</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-400 uppercase">Player</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-400 uppercase">Status</th>
                  <th className="px-4 py-3 text-right text-xs font-semibold text-gray-400 uppercase">Period XP</th>
                  <th className="px-4 py-3 text-right text-xs font-semibold text-gray-400 uppercase">Lifetime XP</th>
                  <th className="px-4 py-3 text-center text-xs font-semibold text-gray-400 uppercase">Valid Through</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-700">
                {filteredPlayers.slice(0, 50).map((player, index) => {
                  const style = getTierStyle(player.tier);
                  
                  return (
                    <tr 
                      key={player.userId}
                      onClick={() => handlePlayerClick(player)}
                      className="hover:bg-gray-700/50 cursor-pointer transition-colors"
                    >
                      <td className="px-4 py-3">
                        <span className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold ${
                          index === 0 ? 'bg-yellow-500/20 text-yellow-400' :
                          index === 1 ? 'bg-gray-400/20 text-gray-300' :
                          index === 2 ? 'bg-amber-600/20 text-amber-500' :
                          'bg-gray-700 text-gray-400'
                        }`}>
                          {index + 1}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 rounded-full bg-gray-700 flex items-center justify-center text-amber-500 font-bold">
                            {player.firstName?.charAt(0) || player.email?.charAt(0) || '?'}
                          </div>
                          <div>
                            <div className="text-white font-medium">
                              {player.firstName} {player.lastName}
                            </div>
                            <div className="text-gray-400 text-xs">{player.memberNumber || player.email}</div>
                          </div>
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <span className={`px-3 py-1 rounded-full text-sm font-medium ${style.bg} ${style.text}`}>
                          {player.displayName}
                        </span>
                        {player.hasLifetimeStatus && (
                          <span className="ml-2 text-purple-400" title={`Lifetime: ${player.lifetimeStatusDisplay}`}>
                            <Star size={14} className="inline" />
                          </span>
                        )}
                      </td>
                      <td className="px-4 py-3 text-right">
                        <div className="text-amber-500 font-bold">{player.currentPeriodXp?.toLocaleString()}</div>
                        {/* XP Progress Bar */}
                        <div className="w-24 h-1 bg-gray-700 rounded-full mt-1 ml-auto">
                          <div 
                            className="h-full bg-amber-500 rounded-full"
                            style={{ width: `${Math.min(100, (player.currentPeriodXp / (xpThresholds?.[xpThresholds.length - 1]?.xpRequired || 10000)) * 100)}%` }}
                          />
                        </div>
                      </td>
                      <td className="px-4 py-3 text-right text-gray-300">
                        {player.lifetimeXp?.toLocaleString()}
                      </td>
                      <td className="px-4 py-3 text-center text-gray-400 text-sm">
                        {player.statusValidThrough 
                          ? new Date(player.statusValidThrough).toLocaleDateString()
                          : '-'
                        }
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          
          {filteredPlayers.length === 0 && (
            <div className="p-8 text-center text-gray-400">
              No players found{selectedTier ? ` in ${selectedTier} tier` : ''}
            </div>
          )}
        </div>

        {/* XP Thresholds Reference */}
        <div className="bg-gray-800 rounded-xl p-6">
          <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
            <Award size={20} className="text-amber-500" />
            XP Thresholds
          </h3>
          
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-2">
            {xpThresholds?.map(threshold => {
              const style = getTierStyle(threshold.tier);
              return (
                <div 
                  key={`${threshold.tier}-${threshold.level}`}
                  className={`p-3 rounded-lg ${style.bg} border ${style.border}`}
                >
                  <div className={`text-sm font-medium ${style.text}`}>{threshold.displayName}</div>
                  <div className="text-white font-bold">{threshold.xpRequired?.toLocaleString()} XP</div>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* Player Achievements Modal */}
      {selectedPlayer && (
        <PlayerAchievementsModal
          player={selectedPlayer}
          data={playerAchievements}
          loading={achievementsLoading}
          onClose={closeModal}
        />
      )}
    </>
  );
}

// ==========================================
// PLAYER ACHIEVEMENTS MODAL
// ==========================================
function PlayerAchievementsModal({ player, data, loading, onClose }) {
  const style = getTierStyle(player?.tier);
  const [xpBreakdownView, setXpBreakdownView] = useState('period');
  const [detailedBreakdown, setDetailedBreakdown] = useState(null);
  const [detailedLoading, setDetailedLoading] = useState(false);
  const [showDetailedBreakdown, setShowDetailedBreakdown] = useState(false);

  // Fetch detailed XP breakdown from API
  const fetchDetailedBreakdown = async () => {
    if (!player?.userId) return;
    setDetailedLoading(true);
    try {
      const response = await api.get(`/status-system/user/${player.userId}/xp-breakdown`);
      setDetailedBreakdown(response.data);
      setShowDetailedBreakdown(true);
    } catch (error) {
      console.error('Error fetching detailed breakdown:', error);
      toast.error('Failed to fetch detailed XP breakdown');
    } finally {
      setDetailedLoading(false);
    }
  };


  // Group achievements by category
  const achievementsByCategory = useMemo(() => {
    if (!data?.achievements) return {};
    
    return data.achievements.reduce((acc, achievement) => {
      const category = achievement.category || 'other';
      if (!acc[category]) acc[category] = [];
      acc[category].push(achievement);
      return acc;
    }, {});
  }, [data?.achievements]);

  // Helper function to format XP breakdown data
  const getXPBreakdown = (view) => {
    // Debug logging
    console.log('getXPBreakdown called with view:', view);
    console.log('data object:', data);
    console.log('status object:', data?.status);
    
    // If status object is not available, return null
    if (!data?.status) {
      console.log('No status object available');
      return null;
    }

    const isPeriod = view === 'period';
    const xpData = isPeriod 
      ? data?.status?.currentPeriod?.xpSystemV2 
      : data?.status?.lifetime?.xpSystemV2;
    
    const achievementXP = isPeriod
      ? (data?.status?.currentPeriod?.achievementXP || 0)
      : (data?.status?.lifetime?.totalAchievementXP || 0);
    
    // Debug logging (after variables are declared)
    console.log('XP System V2 enabled:', xpData?.enabled);
    console.log('Extended Play XP:', xpData?.extendedPlayXP);
    console.log('Extended Play Bonus Config:', data?.status?.xpV2Config?.extendedPlayBonus);

    // If V2 system is not enabled or data is missing, return null for fallback
    if (!xpData || !xpData.enabled) {
      return null;
    }

    const pokerMinutes = xpData.pokerMinutes || 0;
    const calGamesMinutes = xpData.calGamesMinutes || 0;
    const pokerHours = pokerMinutes / 60;
    const calGamesHours = calGamesMinutes / 60;
    const pokerXP = xpData.pokerXP || 0;
    const calGamesXP = xpData.calGamesXP || 0;
    const visitXP = xpData.visitXP || 0;
    const extendedPlayXP = xpData.extendedPlayXP || 0;
    const visitCount = xpData.visitCount || 0;

    // Calculate XP rates
    // For poker: use configured rate from xpV2Config (not calculated average)
    const pokerXpPerHour = data?.status?.xpV2Config?.pokerXpPerHour || 50;
    // For cal games: use calculated average (since it's bet-based tiers)
    const calGamesXpPerHour = calGamesHours > 0 ? calGamesXP / calGamesHours : 0;

    // Get XP per visit from config or use default
    const xpPerVisit = data?.status?.xpV2Config?.xpPerVisit || 25;

    // Get multiplier bonus XP
    const multiplierBonusXP = xpData.multiplierBonusXP || 0;
    const appliedMultipliers = xpData.multipliers || [];

    const activityTotal = pokerXP + calGamesXP + visitXP + extendedPlayXP + multiplierBonusXP;
    const totalXP = activityTotal + achievementXP;

    // Get extended play bonus config for display
    const extBonusConfig = data?.status?.xpV2Config?.extendedPlayBonus;
    const extBonusAmount = extBonusConfig?.xpAmount || 25;
    const extBonusMinutes = extBonusConfig?.minutesRequired || 120;
    // Calculate actual bonus count from earned XP (since bonus is calculated per session)
    // Each bonus is worth extBonusAmount XP, so count = total XP / XP per bonus
    const extBonusCount = extendedPlayXP > 0 && extBonusAmount > 0
      ? Math.round(extendedPlayXP / extBonusAmount)
      : 0;

    // Get achievements for the selected view (period or lifetime)
    const allAchievements = data?.achievements || [];
    let relevantAchievements = [];
    
    if (isPeriod) {
      // For period view, show achievements unlocked during the current period
      // Use achievementIds from status if available (more accurate)
      const periodAchievementIds = data?.status?.currentPeriod?.achievementIds || [];
      
      if (periodAchievementIds.length > 0) {
        // Use achievement IDs from status calculation (authoritative)
        relevantAchievements = allAchievements.filter(ach => 
          ach.unlocked && periodAchievementIds.includes(ach.id)
        );
      } else {
        // Fallback: filter by date if achievementIds not available
        const periodStart = data?.status?.currentPeriod?.startDate;
        const periodEnd = data?.status?.currentPeriod?.endDate;
        
        if (periodStart && periodEnd) {
          relevantAchievements = allAchievements.filter(ach => {
            if (!ach.unlocked || !ach.unlockedAt) return false;
            const unlockedDate = new Date(ach.unlockedAt);
            return unlockedDate >= new Date(periodStart) && unlockedDate <= new Date(periodEnd);
          });
        }
      }
    } else {
      // For lifetime view, show all unlocked achievements
      relevantAchievements = allAchievements.filter(ach => ach.unlocked);
    }
    
    // Calculate total achievement XP for relevant achievements
    const relevantAchievementXP = relevantAchievements.reduce((sum, ach) => sum + (ach.xp || 0), 0);

    return {
      pokerXP,
      calGamesXP,
      visitXP,
      extendedPlayXP,
      multiplierBonusXP,
      appliedMultipliers,
      pokerHours,
      calGamesHours,
      pokerXpPerHour,
      calGamesXpPerHour,
      visitCount,
      xpPerVisit,
      activityTotal,
      achievementXP,
      relevantAchievements, // Achievements for the selected view
      relevantAchievementXP, // Total XP from relevant achievements
      totalXP,
      hasActivity: activityTotal > 0, // Flag to check if there's any activity
      extBonusConfig: extBonusConfig?.enabled ? {
        enabled: true,
        amount: extBonusAmount,
        minutesRequired: extBonusMinutes,
        count: extBonusCount, // Actual count of bonuses earned (calculated from XP)
      } : null,
    };
  };

  const categoryLabels = {
    sessions: { label: 'Session Achievements', icon: Calendar },
    hours: { label: 'Hours Played', icon: Clock },
    points: { label: 'Points Earned', icon: Trophy },
    games: { label: 'Games Played', icon: Gamepad2 },
    tiers: { label: 'Tier Achievements', icon: Crown },
    special: { label: 'Special Achievements', icon: Star },
    other: { label: 'Other Achievements', icon: Award },
  };

  return (
    <>
      {/* Backdrop */}
      <div 
        className="fixed inset-0 bg-black/60 z-40"
        onClick={onClose}
      />
      
      {/* Modal */}
      <div className="fixed inset-y-0 right-0 w-full max-w-lg bg-gray-900 shadow-2xl z-50 overflow-y-auto animate-slide-in-right">
        {/* Header */}
        <div className="sticky top-0 bg-gray-900 border-b border-gray-700 p-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className={`w-12 h-12 rounded-full ${style.bg} ${style.text} flex items-center justify-center font-bold text-xl`}>
              {player?.firstName?.charAt(0) || '?'}
            </div>
            <div>
              <h2 className="text-lg font-bold text-white">{player?.firstName} {player?.lastName}</h2>
              <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${style.bg} ${style.text}`}>
                {player?.displayName}
              </span>
            </div>
          </div>
          <button 
            onClick={onClose}
            className="p-2 rounded-lg hover:bg-gray-800 text-gray-400 hover:text-white transition-colors"
          >
            <X size={20} />
          </button>
        </div>

        {/* Content */}
        <div className="p-4 space-y-4">
          {loading ? (
            <div className="flex flex-col items-center justify-center h-64">
              <RefreshCw className="w-8 h-8 text-amber-500 animate-spin mb-4" />
              <p className="text-gray-400">Loading achievements...</p>
            </div>
          ) : data?.success ? (
            <>
              {/* XP Summary */}
              <div className="grid grid-cols-3 gap-3">
                <div className="bg-gray-800 rounded-xl p-4 text-center">
                  <div className="text-gray-400 text-xs mb-1">Period XP</div>
                  <div className="text-xl font-bold text-amber-500">{(data.status?.currentPeriod?.xp ?? data.player?.currentPeriodXp)?.toLocaleString()}</div>
                </div>
                <div className="bg-gray-800 rounded-xl p-4 text-center">
                  <div className="text-gray-400 text-xs mb-1">Lifetime XP</div>
                  <div className="text-xl font-bold text-white">{(data.status?.lifetime?.xp ?? data.player?.lifetimeXp)?.toLocaleString()}</div>
                </div>
                <div className="bg-gray-800 rounded-xl p-4 text-center">
                  <div className="text-gray-400 text-xs mb-1">From Achievements</div>
                  <div className="text-xl font-bold text-purple-400">{data.summary?.totalXpEarned?.toLocaleString()}</div>
                </div>
              </div>

              {/* XP Breakdown */}
              {(() => {
                const breakdown = getXPBreakdown(xpBreakdownView);
                const periodLabel = data?.status?.currentPeriod?.periodLabel || 
                                   (data?.status?.currentPeriod?.periodId ? 
                                     data.status.currentPeriod.periodId.replace('-', ' ') : 
                                     'Current Period');
                
                return (
                  <div className="bg-gray-800 rounded-xl p-4">
                    {/* Header */}
                    <div className="flex items-center justify-between mb-4">
                      <div className="flex items-center gap-2">
                        <div className="w-12 h-12 rounded-full bg-amber-500/20 flex items-center justify-center">
                          <Zap className="text-amber-500" size={24} />
                        </div>
                        <div>
                          <h3 className="text-lg font-bold text-white">XP Breakdown</h3>
                          <p className="text-xs text-gray-400">Here's how experience points are calculated</p>
                        </div>
                      </div>
                    </div>

                    {/* Period/Lifetime Toggle */}
                    <div className="flex gap-2 mb-4">
                      <button
                        onClick={() => setXpBreakdownView('period')}
                        className={`flex-1 px-4 py-2 rounded-lg font-medium transition-colors ${
                          xpBreakdownView === 'period'
                            ? 'bg-amber-500 text-white'
                            : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
                        }`}
                      >
                        {periodLabel}
                      </button>
                      <button
                        onClick={() => setXpBreakdownView('lifetime')}
                        className={`flex-1 px-4 py-2 rounded-lg font-medium transition-colors ${
                          xpBreakdownView === 'lifetime'
                            ? 'bg-amber-500 text-white'
                            : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
                        }`}
                      >
                        Lifetime
                      </button>
                    </div>

                    {breakdown ? (
                      <>
                        {/* Activity XP Section */}
                        <div className="mb-4">
                          <div className="flex items-center gap-2 mb-3">
                            <h4 className="text-sm font-semibold text-gray-300 uppercase">Activity XP</h4>
                            <Info size={14} className="text-gray-500" />
                          </div>
                          
                          {breakdown.hasActivity ? (
                            <div className="space-y-3">
                              {/* Poker XP */}
                              {breakdown.pokerXP > 0 && (
                                <div className="flex items-center justify-between p-3 bg-gray-700/30 rounded-lg">
                                  <div className="flex items-center gap-3">
                                    <Gamepad2 size={20} className="text-blue-400" />
                                    <div>
                                      <div className="text-sm font-medium text-white">Poker</div>
                                      <div className="text-xs text-gray-400">
                                        {breakdown.pokerHours.toFixed(2)}h Ã— {Math.round(breakdown.pokerXpPerHour)} XP/hr
                                      </div>
                                    </div>
                                  </div>
                                  <div className="text-sm font-bold text-amber-500">
                                    +{breakdown.pokerXP.toLocaleString()}
                                  </div>
                                </div>
                              )}

                              {/* Cal Games XP */}
                              {breakdown.calGamesXP > 0 && (
                                <div className="flex items-center justify-between p-3 bg-gray-700/30 rounded-lg">
                                  <div className="flex items-center gap-3">
                                    <Award size={20} className="text-green-400" />
                                    <div>
                                      <div className="text-sm font-medium text-white">Cal Games</div>
                                      <div className="text-xs text-gray-400">
                                        {breakdown.calGamesHours.toFixed(2)}h
                                        {breakdown.calGamesXpPerHour > 0 && (
                                          <span> Ã— {Math.round(breakdown.calGamesXpPerHour)} XP/hr avg</span>
                                        )}
                                        <span className="text-gray-500 ml-1">(bet-based tiers)</span>
                                      </div>
                                    </div>
                                  </div>
                                  <div className="flex items-center gap-2">
                                    <button
                                      onClick={fetchDetailedBreakdown}
                                      disabled={detailedLoading}
                                      className="text-xs px-2 py-1 bg-gray-600 text-gray-300 rounded hover:bg-gray-500 disabled:opacity-50"
                                    >
                                      {detailedLoading ? '...' : 'Details'}
                                    </button>
                                    <div className="text-sm font-bold text-green-400">
                                      +{breakdown.calGamesXP.toLocaleString()}
                                    </div>
                                  </div>
                                </div>
                              )}

                              {/* Visit Bonus */}
                              {breakdown.visitXP > 0 && (
                                <div className="flex items-center justify-between p-3 bg-gray-700/30 rounded-lg">
                                  <div className="flex items-center gap-3">
                                    <Calendar size={20} className="text-purple-400" />
                                    <div>
                                      <div className="text-sm font-medium text-white">Visit Bonus</div>
                                      <div className="text-xs text-gray-400">
                                        {breakdown.visitCount} Ã— {breakdown.xpPerVisit} XP
                                      </div>
                                    </div>
                                  </div>
                                  <div className="text-sm font-bold text-purple-400">
                                    +{breakdown.visitXP.toLocaleString()}
                                  </div>
                                </div>
                              )}

                              {/* Extended Play Bonus */}
                              {breakdown.extendedPlayXP > 0 && breakdown.extBonusConfig && (
                                <div className="flex items-center justify-between p-3 bg-gray-700/30 rounded-lg">
                                  <div className="flex items-center gap-3">
                                    <Clock size={20} className="text-yellow-400" />
                                    <div>
                                      <div className="text-sm font-medium text-white">Extended Play Bonus</div>
                                      <div className="text-xs text-gray-400">
                                        {breakdown.extBonusConfig.count} Ã— {breakdown.extBonusConfig.amount} XP
                                        <span className="ml-1">({breakdown.extBonusConfig.count} bonus{breakdown.extBonusConfig.count !== 1 ? 'es' : ''} from sessions with {breakdown.extBonusConfig.minutesRequired}+ min)</span>
                                      </div>
                                    </div>
                                  </div>
                                  <div className="text-sm font-bold text-yellow-400">
                                    +{breakdown.extendedPlayXP.toLocaleString()}
                                  </div>
                                </div>
                              )}
                              
                              {/* Show info if extended play bonus is enabled but no bonus earned */}
                              {breakdown.extBonusConfig?.enabled && breakdown.extendedPlayXP === 0 && breakdown.hasActivity && (
                                <div className="flex items-center justify-between p-3 bg-gray-700/20 rounded-lg opacity-60">
                                  <div className="flex items-center gap-3">
                                    <Clock size={20} className="text-gray-500" />
                                    <div>
                                      <div className="text-sm font-medium text-gray-400">Extended Play Bonus</div>
                                      <div className="text-xs text-gray-500">
                                        No bonus earned (requires {breakdown.extBonusConfig.minutesRequired}min per session)
                                      </div>
                                    </div>
                                  </div>
                                  <div className="text-sm font-bold text-gray-500">
                                    +0
                                  </div>
                                </div>
                              )}

                              {/* XP Multiplier Bonus */}
                              {breakdown.multiplierBonusXP > 0 && (
                                <div className="flex items-center justify-between p-3 bg-amber-500/20 rounded-lg border border-amber-500/30">
                                  <div className="flex items-center gap-3">
                                    <Zap size={20} className="text-amber-400" />
                                    <div>
                                      <div className="text-sm font-medium text-white">XP Multiplier Bonus</div>
                                      <div className="text-xs text-amber-400/80">
                                        {breakdown.appliedMultipliers?.map(m => `${m.multiplierValue}x (${m.sessionsAffected || 0} sessions)`).join(', ') || 'Active multiplier'}
                                      </div>
                                    </div>
                                  </div>
                                  <div className="text-sm font-bold text-amber-400">
                                    +{breakdown.multiplierBonusXP.toLocaleString()}
                                  </div>
                                </div>
                              )}

                              {/* Show message if no activity items */}
                              {!breakdown.pokerXP && !breakdown.calGamesXP && !breakdown.visitXP && !breakdown.extendedPlayXP && !breakdown.multiplierBonusXP && (
                                <div className="text-center py-4 text-gray-400 text-sm">
                                  No activity recorded for this period
                                </div>
                              )}
                            </div>
                          ) : (
                            <div className="text-center py-4 text-gray-400 text-sm">
                              No activity recorded for this period
                            </div>
                          )}
                        </div>

                        {/* Activity Total */}
                        <div className="flex items-center justify-between py-3 border-t border-gray-700">
                          <span className="text-sm font-medium text-gray-300">Activity Total</span>
                          <span className="text-sm font-bold text-white">{breakdown.activityTotal.toLocaleString()} XP</span>
                        </div>

                        {/* Achievements Section */}
                        {breakdown.relevantAchievements && breakdown.relevantAchievements.length > 0 && (
                          <>
                            <div className="mt-4 mb-2">
                              <div className="flex items-center gap-2 mb-3">
                                <h4 className="text-sm font-semibold text-gray-300 uppercase">Achievements</h4>
                                <Info size={14} className="text-gray-500" />
                              </div>
                              
                              <div className="space-y-2 max-h-48 overflow-y-auto">
                                {breakdown.relevantAchievements.map(achievement => (
                                  <div 
                                    key={achievement.id}
                                    className="flex items-center justify-between p-2 bg-gray-700/30 rounded-lg"
                                  >
                                    <div className="flex items-center gap-2 flex-1 min-w-0">
                                      <div className="w-8 h-8 rounded-full bg-purple-500/20 flex items-center justify-center flex-shrink-0">
                                        <Award size={14} className="text-purple-400" />
                                      </div>
                                      <div className="flex-1 min-w-0">
                                        <div className="text-xs font-medium text-white truncate">{achievement.name}</div>
                                        {achievement.unlockedAt && (
                                          <div className="text-xs text-gray-500">
                                            {new Date(achievement.unlockedAt).toLocaleDateString()}
                                          </div>
                                        )}
                                      </div>
                                    </div>
                                    <div className="text-xs font-bold text-purple-400 ml-2 flex-shrink-0">
                                      +{achievement.xp?.toLocaleString() || 0}
                                    </div>
                                  </div>
                                ))}
                              </div>
                            </div>

                            {/* Achievements Total */}
                            <div className="flex items-center justify-between py-2 border-t border-gray-700">
                              <span className="text-sm font-medium text-gray-300">Achievements Total</span>
                              <span className="text-sm font-bold text-purple-400">{breakdown.relevantAchievementXP.toLocaleString()} XP</span>
                            </div>
                          </>
                        )}

                        {/* Total XP */}
                        <div className="flex items-center justify-between py-3 border-t border-gray-700">
                          <span className="text-base font-semibold text-white">Total XP</span>
                          <span className="text-xl font-bold text-amber-500">{breakdown.totalXP.toLocaleString()} XP</span>
                        </div>
                      </>
                    ) : (
                      <div className="text-center py-8 text-gray-400 text-sm">
                        <p>XP breakdown not available</p>
                        <p className="text-xs mt-1">
                          {!data?.status 
                            ? 'Status data not loaded' 
                            : data?.status?.currentPeriod?.xpSystemV2?.enabled === false
                            ? 'XP System V2 is disabled'
                            : 'Legacy XP system or no activity data'}
                        </p>
                        {/* Debug info - remove in production */}
                        {process.env.NODE_ENV === 'development' && (
                          <div className="mt-4 text-left text-xs bg-gray-900 p-2 rounded">
                            <p>Debug: status exists = {data?.status ? 'yes' : 'no'}</p>
                            {data?.status && (
                              <>
                                <p>V2 enabled (period) = {data.status.currentPeriod?.xpSystemV2?.enabled ? 'yes' : 'no'}</p>
                                <p>V2 enabled (lifetime) = {data.status.lifetime?.xpSystemV2?.enabled ? 'yes' : 'no'}</p>
                              </>
                            )}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                );
              })()}

              {/* Achievement Progress */}
              <div className="bg-gray-800 rounded-xl p-4">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-gray-400 text-sm">Achievement Progress</span>
                  <span className="text-white font-medium">
                    {data.summary?.unlockedCount} / {data.summary?.totalAchievements}
                  </span>
                </div>
                <div className="w-full h-3 bg-gray-700 rounded-full overflow-hidden">
                  <div 
                    className="h-full bg-gradient-to-r from-amber-500 to-yellow-400 rounded-full transition-all duration-500"
                    style={{ width: `${data.summary?.completionPercent || 0}%` }}
                  />
                </div>
                <div className="text-right text-sm text-amber-500 mt-1">{data.summary?.completionPercent}% Complete</div>
              </div>

              {/* Achievements by Category */}
              <div className="space-y-4">
                {Object.entries(achievementsByCategory).map(([category, achievements]) => {
                  const categoryInfo = categoryLabels[category] || categoryLabels.other;
                  const CategoryIcon = categoryInfo.icon;
                  const unlockedInCategory = achievements.filter(a => a.unlocked).length;
                  
                  return (
                    <div key={category} className="bg-gray-800 rounded-xl overflow-hidden">
                      <div className="p-3 bg-gray-700/50 flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <CategoryIcon size={16} className="text-amber-500" />
                          <span className="font-medium text-white">{categoryInfo.label}</span>
                        </div>
                        <span className="text-sm text-gray-400">{unlockedInCategory}/{achievements.length}</span>
                      </div>
                      
                      <div className="p-3 space-y-2">
                        {achievements.map(achievement => (
                          <div 
                            key={achievement.id}
                            className={`flex items-center gap-3 p-3 rounded-lg transition-colors ${
                              achievement.unlocked 
                                ? 'bg-green-900/20 border border-green-700/30' 
                                : 'bg-gray-700/30 opacity-60'
                            }`}
                          >
                            <div className={`w-10 h-10 rounded-full flex items-center justify-center text-xl ${
                              achievement.unlocked ? 'bg-green-500/20' : 'bg-gray-600/50'
                            }`}>
                              {getAchievementIcon(achievement.icon)}
                            </div>
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2">
                                <span className={`font-medium ${achievement.unlocked ? 'text-white' : 'text-gray-400'}`}>
                                  {achievement.name}
                                </span>
                                {achievement.unlocked ? (
                                  <Check size={14} className="text-green-500" />
                                ) : (
                                  <Lock size={14} className="text-gray-500" />
                                )}
                              </div>
                              <p className="text-xs text-gray-400 truncate">{achievement.description}</p>
                            </div>
                            <div className="text-right">
                              <div className={`text-sm font-bold ${achievement.unlocked ? 'text-amber-500' : 'text-gray-500'}`}>
                                +{achievement.xp} XP
                              </div>
                              {achievement.unlocked && achievement.unlockedAt && (
                                <div className="text-xs text-gray-500">
                                  {new Date(achievement.unlockedAt).toLocaleDateString()}
                                </div>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  );
                })}
              </div>
            </>
          ) : (
            <div className="flex flex-col items-center justify-center h-64 px-6">
              <AlertTriangle className="text-gray-500 mb-4" size={48} />
              <p className="text-gray-400 text-center">Failed to load achievements</p>
            </div>
          )}
        </div>
      </div>
      {/* Detailed XP Breakdown Modal */}
      {showDetailedBreakdown && detailedBreakdown && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
          <div className="bg-gray-800 rounded-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-gray-800 p-4 border-b border-gray-700 flex justify-between items-center">
              <h3 className="text-lg font-bold text-white">Detailed XP Breakdown</h3>
              <button onClick={() => setShowDetailedBreakdown(false)} className="text-gray-400 hover:text-white">
                <X size={20} />
              </button>
            </div>
            <div className="p-4 space-y-6">
              {/* Period/Lifetime Toggle */}
              <div className="flex gap-2">
                <button
                  onClick={() => setXpBreakdownView('period')}
                  className={`px-4 py-2 rounded-lg text-sm font-medium ${xpBreakdownView === 'period' ? 'bg-amber-500 text-black' : 'bg-gray-700 text-gray-300'}`}
                >
                  {detailedBreakdown?.config?.resetMode === 'quarterly' ? 'Q1 2026' : '2026'}
                </button>
                <button
                  onClick={() => setXpBreakdownView('lifetime')}
                  className={`px-4 py-2 rounded-lg text-sm font-medium ${xpBreakdownView === 'lifetime' ? 'bg-amber-500 text-black' : 'bg-gray-700 text-gray-300'}`}
                >
                  Lifetime
                </button>
              </div>
              
              {(() => {
                const bd = xpBreakdownView === 'period' ? detailedBreakdown.period : detailedBreakdown.lifetime;
                if (!bd) return null;
                
                return (
                  <div className="space-y-4">
                    {/* Poker */}
                    <div className="bg-gray-700/50 rounded-lg p-4">
                      <div className="flex justify-between items-center mb-2">
                        <span className="text-amber-400 font-medium">Poker</span>
                        <span className="text-green-400 font-bold">+{bd.poker.xp.toLocaleString()} XP</span>
                      </div>
                      <div className="text-sm text-gray-400">
                        {bd.poker.sessions} sessions - {bd.poker.hours}h x {detailedBreakdown.config.pokerXpPerHour} XP/hr
                      </div>
                    </div>
                    
                    {/* Cal Games */}
                    <div className="bg-gray-700/50 rounded-lg p-4">
                      <div className="flex justify-between items-center mb-2">
                        <span className="text-green-400 font-medium">Cal Games</span>
                        <span className="text-green-400 font-bold">+{bd.calGames.total.xp.toLocaleString()} XP</span>
                      </div>
                      <div className="text-sm text-gray-400 mb-3">
                        {bd.calGames.total.sessions} sessions - {bd.calGames.total.hours}h - Avg {bd.calGames.total.avgXpPerHour} XP/hr
                      </div>
                      
                      {/* Tier Breakdown Table */}
                      <div className="bg-gray-800/50 rounded-lg overflow-hidden">
                        <table className="w-full text-sm">
                          <thead>
                            <tr className="border-b border-gray-600">
                              <th className="text-left p-2 text-gray-400 font-medium">Tier</th>
                              <th className="text-right p-2 text-gray-400 font-medium">Sessions</th>
                              <th className="text-right p-2 text-gray-400 font-medium">Hours</th>
                              <th className="text-right p-2 text-gray-400 font-medium">Rate</th>
                              <th className="text-right p-2 text-gray-400 font-medium">XP</th>
                            </tr>
                          </thead>
                          <tbody>
                            {bd.calGames.noBetData.sessions > 0 && (
                              <tr className="border-b border-gray-700/50">
                                <td className="p-2 text-gray-300">No bet data</td>
                                <td className="p-2 text-right text-gray-300">{bd.calGames.noBetData.sessions}</td>
                                <td className="p-2 text-right text-gray-300">{bd.calGames.noBetData.hours}</td>
                                <td className="p-2 text-right text-gray-400">100/hr</td>
                                <td className="p-2 text-right text-green-400">{bd.calGames.noBetData.xp.toLocaleString()}</td>
                              </tr>
                            )}
                            {Object.entries(bd.calGames.tiers).map(([label, tier]) => (
                              tier.sessions > 0 && (
                                <tr key={label} className="border-b border-gray-700/50">
                                  <td className="p-2 text-gray-300">{label}</td>
                                  <td className="p-2 text-right text-gray-300">{tier.sessions}</td>
                                  <td className="p-2 text-right text-gray-300">{tier.hours}</td>
                                  <td className="p-2 text-right text-gray-400">{tier.xpPerHour}/hr</td>
                                  <td className="p-2 text-right text-green-400">{tier.xp.toLocaleString()}</td>
                                </tr>
                              )
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                    
                    {/* Visit Bonus */}
                    <div className="bg-gray-700/50 rounded-lg p-4">
                      <div className="flex justify-between items-center">
                        <span className="text-purple-400 font-medium">Visit Bonus</span>
                        <span className="text-green-400 font-bold">+{bd.visits.xp.toLocaleString()} XP</span>
                      </div>
                      <div className="text-sm text-gray-400">
                        {bd.visits.count} visits x {detailedBreakdown.config.xpPerVisit} XP
                      </div>
                    </div>
                    
                    {/* Extended Play */}
                    <div className="bg-gray-700/50 rounded-lg p-4">
                      <div className="flex justify-between items-center">
                        <span className="text-blue-400 font-medium">Extended Play Bonus</span>
                        <span className="text-green-400 font-bold">+{bd.extendedPlay.xp.toLocaleString()} XP</span>
                      </div>
                      <div className="text-sm text-gray-400">
                        {bd.extendedPlay.bonusCount} sessions with 120+ min x {detailedBreakdown.config.extendedPlayBonus?.xpAmount || 25} XP
                      </div>
                    </div>
                    
                    {/* XP Multiplier Bonus */}
                    {bd.multiplierBonus?.xp > 0 && (
                      <div className="bg-amber-500/20 rounded-lg p-4 border border-amber-500/30">
                        <div className="flex justify-between items-center">
                          <span className="text-amber-400 font-medium">âš¡ XP Multiplier Bonus</span>
                          <span className="text-amber-400 font-bold">+{bd.multiplierBonus.xp.toLocaleString()} XP</span>
                        </div>
                        <div className="text-sm text-amber-400/70">
                          {bd.multiplierBonus.multipliers?.map(m => 
                            `${m.multiplierValue}x (${m.sessionsAffected || 0} sessions)`
                          ).join(', ') || 'Active multiplier applied'}
                        </div>
                      </div>
                    )}
                    
                    {/* Achievements */}
                    <div className="bg-gray-700/50 rounded-lg p-4">
                      <div className="flex justify-between items-center">
                        <span className="text-yellow-400 font-medium">Achievements</span>
                        <span className="text-green-400 font-bold">+{bd.achievements.xp.toLocaleString()} XP</span>
                      </div>
                      <div className="text-sm text-gray-400">
                        {bd.achievements.count} achievements earned
                      </div>
                    </div>
                    
                    {/* Total */}
                    <div className="bg-amber-500/20 border border-amber-500/30 rounded-lg p-4">
                      <div className="flex justify-between items-center">
                        <span className="text-amber-400 font-bold text-lg">Total XP</span>
                        <span className="text-amber-400 font-bold text-xl">{bd.totalXP.toLocaleString()} XP</span>
                      </div>
                      {detailedBreakdown.member && (
                        <div className="text-sm text-gray-400 mt-1 flex items-center justify-between">
                          <div>
                            Stored: {xpBreakdownView === 'period' ? detailedBreakdown.member.storedPeriodXP?.toLocaleString() : detailedBreakdown.member.storedLifetimeXP?.toLocaleString()} XP
                            {Math.abs(bd.totalXP - (xpBreakdownView === 'period' ? detailedBreakdown.member.storedPeriodXP : detailedBreakdown.member.storedLifetimeXP)) > 10 && (
                              <span className="text-yellow-400 ml-2">(mismatch!)</span>
                            )}
                          </div>
                          {Math.abs(bd.totalXP - (xpBreakdownView === 'period' ? detailedBreakdown.member.storedPeriodXP : detailedBreakdown.member.storedLifetimeXP)) > 10 && (
                            <button
                              onClick={async () => {
                                try {
                                  await api.post(`/status-system/user/${player.userId}/refresh`);
                                  toast.success('XP recalculated - refreshing...');
                                  setTimeout(() => fetchDetailedBreakdown(), 1000);
                                } catch (err) {
                                  toast.error('Failed to recalculate: ' + err.message);
                                }
                              }}
                              className="text-xs px-2 py-1 bg-amber-500/30 text-amber-400 rounded hover:bg-amber-500/50"
                            >
                              Recalculate
                            </button>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                );
              })()}
            </div>
          </div>
        </div>
      )}

      {/* Per-Player Hard Reset Button */}
      {detailedBreakdown && player && (
        <div className="mt-4 pt-4 border-t border-gray-700">
          <button
            onClick={async () => {
              if (!window.confirm(`Full reset for ${player.firstName} ${player.lastName}? This clears status history, achievements, and recalculates everything from start date.`)) return;
              try {
                const res = await api.post(`/admin/reports/full-reset-user/${player.userId}`);
                const d = res.data;
                toast.success(`Reset complete: ${d.achievementsRestored} achievements, tier: ${d.newTier}, ${d.newXP} XP`);
                setTimeout(() => fetchDetailedBreakdown(), 1000);
              } catch (err) {
                toast.error('Failed to reset: ' + (err.response?.data?.error || err.message));
              }
            }}
            className="text-xs px-3 py-1.5 bg-red-500/20 text-red-400 rounded hover:bg-red-500/30 transition-colors"
          >
            Hard Reset (XP + Achievements + Tier History)
          </button>
        </div>
      )}
    </>
  );
}


// ==========================================
// HOURLY HEATMAP REPORT
// ==========================================
function HourlyHeatmapReport({ data, loading }) {
  if (loading) {
    return (
      <div className="bg-gray-800 rounded-xl p-16 flex flex-col items-center justify-center">
        <RefreshCw className="w-8 h-8 text-amber-500 animate-spin mb-4" />
        <p className="text-gray-400">Loading heatmap data...</p>
      </div>
    );
  }

  // API returns heatmapGrid with day names as keys
  const heatmapGrid = data?.heatmapGrid || data?.heatmap;
  
  if (!heatmapGrid) {
    return (
      <div className="bg-gray-800 rounded-xl p-16 flex flex-col items-center justify-center">
        <Grid3X3 className="w-12 h-12 text-gray-600 mb-3" />
        <p className="text-gray-400">No activity data found for this date range</p>
      </div>
    );
  }

  const dayNames = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
  const dayAbbrev = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
  const hours = Array.from({ length: 24 }, (_, i) => i);

  // Find max value for color scaling
  const maxValue = data.maxSessionCount || Math.max(
    ...Object.values(heatmapGrid).flatMap(day => 
      Object.values(day).map(cell => cell?.sessionCount || cell || 0)
    ),
    1
  );

  const getColor = (value) => {
    const v = typeof value === 'object' ? value?.sessionCount : value;
    if (!v || v === 0) return 'bg-gray-800';
    const intensity = v / maxValue;
    if (intensity > 0.8) return 'bg-amber-500';
    if (intensity > 0.6) return 'bg-amber-600';
    if (intensity > 0.4) return 'bg-amber-700';
    if (intensity > 0.2) return 'bg-amber-800';
    return 'bg-amber-900';
  };

  const getValue = (dayName, hour) => {
    const cell = heatmapGrid[dayName]?.[hour];
    return typeof cell === 'object' ? cell?.sessionCount || 0 : cell || 0;
  };

  const formatHour = (hour) => {
    if (hour === 0) return '12a';
    if (hour === 12) return '12p';
    return hour < 12 ? `${hour}a` : `${hour - 12}p`;
  };

  // Get busiest hour and day from API response
  const busiestHour = data.busiestHours?.[0];
  const busiestDay = data.dayOfWeekSummary?.[0];

  return (
    <>
      {/* Summary */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="bg-gray-800 rounded-xl p-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-amber-500/20 rounded-lg flex items-center justify-center">
              <Clock className="w-5 h-5 text-amber-500" />
            </div>
            <div>
              <div className="text-2xl font-bold text-white">{busiestHour?.hourFormatted || '-'}</div>
              <div className="text-sm text-gray-400">Peak Hour</div>
            </div>
          </div>
        </div>
        <div className="bg-gray-800 rounded-xl p-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-green-500/20 rounded-lg flex items-center justify-center">
              <Calendar className="w-5 h-5 text-green-500" />
            </div>
            <div>
              <div className="text-2xl font-bold text-white">{busiestDay?.dayName || '-'}</div>
              <div className="text-sm text-gray-400">Busiest Day</div>
            </div>
          </div>
        </div>
        <div className="bg-gray-800 rounded-xl p-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-blue-500/20 rounded-lg flex items-center justify-center">
              <Users className="w-5 h-5 text-blue-500" />
            </div>
            <div>
              <div className="text-2xl font-bold text-white">{busiestHour?.uniquePlayers || 0}</div>
              <div className="text-sm text-gray-400">Peak Hour Players</div>
            </div>
          </div>
        </div>
      </div>

      {/* Heatmap Grid */}
      <div className="bg-gray-800 rounded-xl overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-700">
          <h2 className="text-lg font-semibold text-white flex items-center gap-2">
            <Grid3X3 className="text-amber-500" size={20} />
            Activity Heatmap
          </h2>
          <p className="text-sm text-gray-400 mt-1">
            Player activity by hour and day of week
          </p>
        </div>

        <div className="p-6 overflow-x-auto">
          <div className="min-w-[800px]">
            {/* Hour labels */}
            <div className="flex mb-2 ml-12">
              {hours.map(hour => (
                <div key={hour} className="flex-1 text-center text-xs text-gray-500">
                  {formatHour(hour)}
                </div>
              ))}
            </div>

            {/* Heatmap rows */}
            {dayNames.map((dayName, dayIndex) => (
              <div key={dayName} className="flex items-center mb-1">
                <div className="w-12 text-sm text-gray-400 font-medium">{dayAbbrev[dayIndex]}</div>
                <div className="flex-1 flex gap-1">
                  {hours.map(hour => {
                    const value = getValue(dayName, hour);
                    return (
                      <div
                        key={hour}
                        className={`flex-1 h-8 rounded ${getColor(value)} transition-colors hover:ring-2 hover:ring-white/30 cursor-pointer`}
                        title={`${dayName} ${formatHour(hour)}: ${value} sessions`}
                      />
                    );
                  })}
                </div>
              </div>
            ))}

            {/* Legend */}
            <div className="flex items-center justify-center gap-4 mt-6 text-sm text-gray-400">
              <span>Less</span>
              <div className="flex gap-1">
                <div className="w-6 h-4 rounded bg-gray-800" />
                <div className="w-6 h-4 rounded bg-amber-900" />
                <div className="w-6 h-4 rounded bg-amber-800" />
                <div className="w-6 h-4 rounded bg-amber-700" />
                <div className="w-6 h-4 rounded bg-amber-600" />
                <div className="w-6 h-4 rounded bg-amber-500" />
              </div>
              <span>More</span>
            </div>
          </div>
        </div>
      </div>

      {/* Busiest Hours */}
      {data?.busiestHours?.length > 0 && (
        <div className="bg-gray-800 rounded-xl overflow-hidden">
          <div className="px-6 py-4 border-b border-gray-700">
            <h2 className="text-lg font-semibold text-white flex items-center gap-2">
              <TrendingUp className="text-amber-500" size={20} />
              Busiest Hours
            </h2>
          </div>
          <div className="p-6">
            <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
              {data.busiestHours.map((peak, index) => (
                <div key={index} className="bg-gray-700 rounded-lg p-4">
                  <div className="text-lg font-bold text-white">{peak.hourFormatted}</div>
                  <div className="text-amber-500 font-medium">{peak.sessionCount} sessions</div>
                  <div className="text-sm text-gray-400">{peak.uniquePlayers} players</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Day of Week Summary */}
      {data?.dayOfWeekSummary?.length > 0 && (
        <div className="bg-gray-800 rounded-xl overflow-hidden">
          <div className="px-6 py-4 border-b border-gray-700">
            <h2 className="text-lg font-semibold text-white flex items-center gap-2">
              <Calendar className="text-amber-500" size={20} />
              Activity by Day of Week
            </h2>
          </div>
          <div className="p-6">
            <div className="grid grid-cols-2 md:grid-cols-7 gap-3">
              {data.dayOfWeekSummary.map((day, index) => (
                <div key={index} className={`rounded-lg p-4 ${index === 0 ? 'bg-amber-500/20 border border-amber-500/50' : 'bg-gray-700'}`}>
                  <div className={`text-sm font-bold ${index === 0 ? 'text-amber-400' : 'text-white'}`}>{day.dayName}</div>
                  <div className="text-lg font-bold text-white mt-1">{day.sessionCount}</div>
                  <div className="text-xs text-gray-400">sessions</div>
                  <div className="text-sm text-gray-300 mt-1">{day.uniquePlayers} players</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </>
  );

}


// ==========================================
// TABLE PLAYERS MODAL
// ==========================================
function TablePlayersModal({ visible, onClose, table, data, loading, onPlayerClick, formatHours }) {
  if (!visible) return null;

  // Tier colors
  const TIER_COLORS = {
    Legend: '#B9F2FF',
    "Chairman's Circle": '#E5E4E2',
    Ambassador: '#FFD700',
    Elite: '#C0C0C0',
    VIP: '#CD7F32',
  };

  return (
    <>
      {/* Backdrop */}
      <div className="fixed inset-0 bg-black/50 z-50" onClick={onClose} />
      
      {/* Modal */}
      <div className="fixed inset-y-0 right-0 w-full max-w-lg bg-gray-800 shadow-2xl z-50 overflow-hidden flex flex-col animate-slide-in-right">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-700">
          <div>
            <h2 className="text-lg font-semibold text-white flex items-center gap-2">
              <Users className="text-amber-500" size={20} />
              Table {table?.tableName} Players
            </h2>
            <p className="text-sm text-gray-400 mt-1">{table?.gameName}</p>
          </div>
          <button
            onClick={onClose}
            className="p-2 text-gray-400 hover:text-white hover:bg-gray-700 rounded-lg transition-colors"
          >
            <X size={20} />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          {loading ? (
            <div className="flex flex-col items-center justify-center h-64">
              <RefreshCw className="w-10 h-10 text-amber-500 animate-spin mb-4" />
              <p className="text-gray-400">Loading players...</p>
            </div>
          ) : !data?.players?.length ? (
            <div className="flex flex-col items-center justify-center h-64">
              <Users className="text-gray-600 mb-4" size={48} />
              <p className="text-gray-400">No players found for this table</p>
            </div>
          ) : (
            <>
              {/* Summary */}
              <div className="grid grid-cols-2 gap-4 mb-6">
                <div className="bg-gray-700/50 rounded-lg p-4 text-center">
                  <div className="text-2xl font-bold text-white">{data.playerCount}</div>
                  <div className="text-xs text-gray-400">Total Players</div>
                </div>
                <div className="bg-gray-700/50 rounded-lg p-4 text-center">
                  <div className="text-2xl font-bold text-amber-500">
                    {data.players.reduce((sum, p) => sum + (p.sessionCount || 0), 0)}
                  </div>
                  <div className="text-xs text-gray-400">Total Sessions</div>
                </div>
              </div>

              <p className="text-sm text-gray-500 mb-4">Click on a player to see detailed stats</p>

              {/* Player List */}
              <div className="space-y-3">
                {data.players.map((player, index) => {
                  const tierColor = (TIER_COLORS[resolveTierName(player.tier).toLowerCase()] || TIER_COLORS.vip).hex;
                  return (
                    <div
                      key={player.playerId || index}
                      onClick={() => onPlayerClick(player)}
                      className="bg-gray-700/50 rounded-lg p-4 cursor-pointer hover:bg-gray-700 transition-colors"
                    >
                      <div className="flex items-center gap-3 mb-3">
                        <div 
                          className="w-10 h-10 rounded-full flex items-center justify-center text-sm font-bold"
                          style={{ backgroundColor: tierColor + '30', color: tierColor }}
                        >
                          {player.firstName?.charAt(0) || ''}{player.lastName?.charAt(0) || ''}
                        </div>
                        <div className="flex-1">
                          <div className="font-medium text-white">{player.firstName} {player.lastName}</div>
                          <div className="flex items-center gap-2 text-sm">
                            <span className="text-gray-500">#{player.memberNumber}</span>
                            <span 
                              className="px-2 py-0.5 rounded text-xs font-medium"
                              style={{ backgroundColor: tierColor + '20', color: tierColor }}
                            >
                              {resolveTierName(player.tier)}
                            </span>
                          </div>
                        </div>
                        <ChevronDown className="text-gray-400 rotate-[-90deg]" />
                      </div>
                      <div className="grid grid-cols-3 gap-4 text-sm">
                        <div>
                          <div className="text-gray-400">Time at Table</div>
                          <div className="font-medium text-green-400">{formatHours(player.totalMinutes / 60)}</div>
                        </div>
                        <div>
                          <div className="text-gray-400">Sessions</div>
                          <div className="font-medium text-white">{player.sessionCount}</div>
                        </div>
                        <div>
                          <div className="text-gray-400">Points Earned</div>
                          <div className="font-medium text-amber-500">{player.totalPoints?.toLocaleString()}</div>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </>
          )}
        </div>
      </div>

      <style>{`
        @keyframes slideInRight {
          from { transform: translateX(100%); }
          to { transform: translateX(0); }
        }
        .animate-slide-in-right {
          animation: slideInRight 0.3s ease-out forwards;
        }
      `}</style>
    </>
  );
}

// ============================================================
// Road to WSOP Leaderboard Report Component
// ============================================================
function WsopLeaderboardReport({ data, loading }) {
  const QUALIFY_COUNT = 9;

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <RefreshCw size={28} className="animate-spin text-amber-500" />
      </div>
    );
  }

  if (!data) return null;

  const { leaderboard = [], promoName, promoStart, promoEnd, promoStatus, totalPlayers, lastUpdated } = data;

  const statusColors = {
    active:   'bg-green-500/20 text-green-400 border border-green-500/30',
    upcoming: 'bg-blue-500/20 text-blue-400 border border-blue-500/30',
    ended:    'bg-gray-500/20 text-gray-400 border border-gray-500/30',
  };

  const rankStyle = (rank) => {
    if (rank === 1) return 'text-yellow-400 font-bold text-lg';
    if (rank === 2) return 'text-gray-300 font-bold';
    if (rank === 3) return 'text-amber-600 font-bold';
    return 'text-gray-400';
  };

  const rankIcon = (rank) => {
    if (rank === 1) return 'ðŸ¥‡';
    if (rank === 2) return 'ðŸ¥ˆ';
    if (rank === 3) return 'ðŸ¥‰';
    if (rank <= QUALIFY_COUNT) return 'âœ…';
    return null;
  };

  return (
    <div className="space-y-6">
      {/* Header card */}
      <div className="bg-gray-800 rounded-xl p-6">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div>
            <h2 className="text-xl font-bold text-white flex items-center gap-2">
              <Trophy size={22} className="text-amber-500" />
              {promoName || 'Road to WSOP'}
            </h2>
            <p className="text-gray-400 text-sm mt-1">
              {promoStart} â†’ {promoEnd}
              {lastUpdated && (
                <span className="ml-3 text-gray-500">
                  Updated {new Date(lastUpdated).toLocaleString('en-US', { timeZone: 'America/Los_Angeles', month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })} PT
                </span>
              )}
            </p>
          </div>
          <div className="flex items-center gap-3">
            <span className={`text-xs px-3 py-1 rounded-full font-medium capitalize ${statusColors[promoStatus] || statusColors.active}`}>
              {promoStatus}
            </span>
            <span className="text-xs px-3 py-1 rounded-full bg-gray-700 text-gray-300">
              {totalPlayers} players ranked
            </span>
          </div>
        </div>
        <div className="mt-4 p-3 rounded-lg bg-amber-500/10 border border-amber-500/20 text-amber-300 text-sm">
          ðŸ† Top <strong>{QUALIFY_COUNT} players</strong> qualify for the WSOP Poker Play-Offs. XP includes Late Night 3Ã— bonus (midnightâ€“10 AM PT).
        </div>
      </div>

      {/* Leaderboard table */}
      <div className="bg-gray-800 rounded-xl overflow-hidden">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-gray-700 text-gray-400 text-xs uppercase tracking-wide">
              <th className="text-left px-6 py-3 w-16">Rank</th>
              <th className="text-left px-6 py-3">Player</th>
              <th className="text-left px-4 py-3">Member #</th>
              <th className="text-right px-6 py-3">XP</th>
              <th className="text-right px-4 py-3">Sessions</th>
              <th className="text-right px-6 py-3">Hours</th>
            </tr>
          </thead>
          <tbody>
            {leaderboard.map((player) => {
              const qualifies = player.rank <= QUALIFY_COUNT;
              return (
                <tr
                  key={player.memberNumber}
                  className={`border-b border-gray-700/50 transition-colors ${
                    qualifies ? 'hover:bg-amber-500/5' : 'hover:bg-gray-700/30 opacity-70'
                  } ${player.rank === QUALIFY_COUNT ? 'border-b-2 border-amber-500/40' : ''}`}
                >
                  <td className="px-6 py-4">
                    <span className={rankStyle(player.rank)}>
                      {rankIcon(player.rank) ? (
                        <span>{rankIcon(player.rank)} {player.rank}</span>
                      ) : (
                        <span className="text-gray-500">{player.rank}</span>
                      )}
                    </span>
                  </td>
                  <td className="px-6 py-4">
                    <span className={`font-medium ${qualifies ? 'text-white' : 'text-gray-400'}`}>
                      {player.fullName}
                    </span>
                  </td>
                  <td className="px-4 py-4 text-gray-500 font-mono text-xs">{player.memberNumber}</td>
                  <td className="px-6 py-4 text-right">
                    <span className={`font-bold ${qualifies ? 'text-amber-400' : 'text-gray-500'}`}>
                      {player.xp.toLocaleString()}
                    </span>
                  </td>
                  <td className="px-4 py-4 text-right text-gray-400">{player.sessions}</td>
                  <td className="px-6 py-4 text-right text-gray-400">{player.hours}h</td>
                </tr>
              );
            })}
            {leaderboard.length === 0 && (
              <tr>
                <td colSpan={6} className="text-center text-gray-500 py-12">
                  No leaderboard data yet.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}


// ============================================================
// WPT Seat Qualifier - Monthly Leaderboards Report Component
// ============================================================
function WptMonthlyReport({ data, loading }) {
  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <RefreshCw size={28} className="animate-spin text-amber-500" />
      </div>
    );
  }

  if (!data) return null;

  const { promoName, monthlyChips = [1500, 1000, 500], months = [] } = data;

  const rankStyle = (rank) => {
    if (rank === 1) return 'text-yellow-400 font-bold text-lg';
    if (rank === 2) return 'text-gray-300 font-bold';
    if (rank === 3) return 'text-amber-600 font-bold';
    return 'text-gray-400';
  };

  return (
    <div className="space-y-6">
      {/* Header card */}
      <div className="bg-gray-800 rounded-xl p-6">
        <h2 className="text-xl font-bold text-white flex items-center gap-2">
          <Crown size={22} className="text-amber-500" />
          {promoName || 'WPT Seat Qualifier'} &mdash; Monthly Leaderboards
        </h2>
        <div className="mt-4 p-3 rounded-lg bg-amber-500/10 border border-amber-500/20 text-amber-300 text-sm">
          Each month's top 3 earn bonus chips toward their final-event stack:{' '}
          <strong>{(monthlyChips[0] || 0).toLocaleString()}</strong> /{' '}
          <strong>{(monthlyChips[1] || 0).toLocaleString()}</strong> /{' '}
          <strong>{(monthlyChips[2] || 0).toLocaleString()}</strong> chips. Points use the 3x (12-6 AM) /
          5x (6 AM-6 PM) / 1x (6 PM-12 AM) Pacific multipliers.
        </div>
      </div>

      {months.length === 0 && (
        <div className="bg-gray-800 rounded-xl p-12 text-center text-gray-500">
          No monthly data yet.
        </div>
      )}

      {months.map((m) => (
        <div key={m.month} className="bg-gray-800 rounded-xl overflow-hidden">
          <div className="px-6 py-4 border-b border-gray-700 flex items-center justify-between">
            <h3 className="text-lg font-semibold text-white">{m.label}</h3>
            <span className="text-xs px-3 py-1 rounded-full bg-gray-700 text-gray-300">
              {m.totalPlayers} players
            </span>
          </div>
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-gray-700 text-gray-400 text-xs uppercase tracking-wide">
                <th className="text-left px-6 py-3 w-16">Rank</th>
                <th className="text-left px-6 py-3">Player</th>
                <th className="text-left px-4 py-3">Member #</th>
                <th className="text-right px-6 py-3">XP</th>
                <th className="text-right px-6 py-3">Bonus Chips</th>
              </tr>
            </thead>
            <tbody>
              {m.standings.map((p) => {
                const awarded = p.bonusChips > 0;
                return (
                  <tr
                    key={p.memberNumber}
                    className={`border-b border-gray-700/50 transition-colors ${
                      awarded ? 'hover:bg-amber-500/5' : 'hover:bg-gray-700/30'
                    } ${p.rank === 3 ? 'border-b-2 border-amber-500/40' : ''}`}
                  >
                    <td className="px-6 py-4">
                      <span className={rankStyle(p.rank)}>{p.rank}</span>
                    </td>
                    <td className="px-6 py-4">
                      <span className={`font-medium ${awarded ? 'text-white' : 'text-gray-300'}`}>
                        {p.firstName} {p.lastName}
                      </span>
                      {p.role && p.role !== 'user' && (
                        <span className="ml-2 text-[10px] uppercase tracking-wide px-1.5 py-0.5 rounded bg-gray-700 text-gray-400">
                          staff
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-4 text-gray-500 font-mono text-xs">{p.memberNumber}</td>
                    <td className="px-6 py-4 text-right">
                      <span className={`font-bold ${awarded ? 'text-amber-400' : 'text-gray-400'}`}>
                        {p.xp.toLocaleString()}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-right">
                      {awarded ? (
                        <span className="font-semibold text-green-400">+{p.bonusChips.toLocaleString()}</span>
                      ) : (
                        <span className="text-gray-600">&mdash;</span>
                      )}
                    </td>
                  </tr>
                );
              })}
              {m.standings.length === 0 && (
                <tr>
                  <td colSpan={5} className="text-center text-gray-500 py-8">No players this month.</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      ))}
    </div>
  );
}
